import { useCallback, useEffect, useRef, useMemo, useState } from 'react';
import useAME from '../../hooks/useAME';
import AMESelect from '../AME/AMESelect';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { GridRowModes, GridToolbarContainer, GridActionsCellItem, useGridApiRef } from '@mui/x-data-grid-premium';
import { Field, FieldArray, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import { multiply, round, divide, add, subtract } from 'mathjs';
import { Box, IconButton, Tooltip } from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import EditIcon from '@mui/icons-material/Edit';
import GridCheckIcon from '@mui/icons-material/Check';
import GridCloseIcon from '@mui/icons-material/Close';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';

import {
  dataGridPremiumColumns,
  generateAutocompleteItems,
  normalizeNumberInput,
  parseLocaleNumber,
} from '../../utils/helper';
import {
  FormErrorUI,
  InputUI,
  AutoCompleteSelectUI,
  AutoCompleteSelectServiceUI,
  AddButtonUi,
  DatagridPremiumUi,
  DatePickerUI,
  CheckboxUI,
  ConfirmDialogUI,
  OrderArrowsUI,
} from '../Interface';
import useConfig from '../../context/ConfigContext';
import useUsers from '../../context/UsersContext';
import { formatCurrency, getRounding } from '../../utils/currencyHelper';
import priceListService from '../../service/priceList.service';
import { DocumentDirection } from '../../config/DocumentDirection';
import { DocStatus, DocStatusList } from '../../config/document';
import { InvManagementMethod, ItemGroupType } from '../../config/itemGroup';
import { NotificationType } from '../../config';
import addNotification from '../../utils/addNotification';
import useGenerateJokerCols from '../../hooks/useGenerateJokerCols';
import discountRuleService from '../../service/discountRule.service';
import { getLineEditableFields, isCellEditable } from '../../utils/DocHelper';
import dayjs from 'dayjs';
import { getProjectOrderNumberName } from '../../utils/getProjectOrderNumberName';
import useParam from '../../context/ParamContext';
import ItemDialog from '../Item/ItemDialog';
import ItemStockDialog from '../Item/ItemStockDialog';
import DocumentPrevPricesDialog from './DocumentPrevPricesDialog';
import masterDataService from '../../service/masterData.service';
import vatCodeService from '../../service/vatCode.service';
import { PriceFormField } from '../../config/priceList';
import { useDocumentLineGridSx } from './style/documentLineGridSx';
import { DocumentType } from '../../config/DocumentType';
import useWindowSubtractedHeight from '../../hooks/useWindowSubtractedHeight';
import { AreaType } from '../../config/companyGroup';
import itemCompanyCatalogService from '../../service/itemCompanyCatalog.service';
import ItemCompanyCatalogDialog from './ItemCompanyCatalogDialog';
import { itemAutocompleteRequestBuilder, itemAutocompleteService } from '../../utils/itemAutocomplete';
import FilePresentOutlinedIcon from '@mui/icons-material/FilePresentOutlined';
import SupportingDocumentAssignmentDialog from '../SupportingDocumentType/SupportingDocumentAssignmentDialog';
import supportingDocumentTypeService from '../../service/supportingDocumentType.service';
import { TemplateDocumentLevel, TemplateType } from '../../config/template';
import TextTemplateMenu from './TextTemplateMenu';

const PRICE_INPUT_PATTERN = /^-?\d*(?:\.\d*)?$/;
const DOWN_PAYMENT_DOCUMENT_TYPES = [DocumentType.DownPayment, DocumentType.APDownPayment];

const isDownPaymentApplicationLine = (line) =>
  DOWN_PAYMENT_DOCUMENT_TYPES.includes(Number(line?.baseDocType));

const toAbsoluteAmount = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? Math.abs(amount) : 0;
};

const getOpenDownPaymentAmount = (grossAmount, appliedGrossAmount) =>
  Math.max(toAbsoluteAmount(grossAmount) - toAbsoluteAmount(appliedGrossAmount), 0);

const isIncompletePriceInput = (value) => value === '' || value === '-' || value.endsWith('.');

const shouldPreservePriceInput = (value, decimalPlaces) => {
  const decimalPart = value.split('.')[1];
  return decimalPart?.endsWith('0') && decimalPart.length < decimalPlaces;
};

const getDocLineVisOrder = (line, fallback = 1) => {
  if (line?.visOrder !== null && line?.visOrder !== undefined && line?.visOrder !== '') {
    const visOrder = Number(line.visOrder);
    if (Number.isFinite(visOrder)) return visOrder;
  }

  if (line?.lineNum !== null && line?.lineNum !== undefined && line?.lineNum !== '') {
    const lineNum = Number(line.lineNum);
    if (Number.isFinite(lineNum)) return lineNum + 1;
  }

  return fallback;
};

const getOrderedDocLines = (lines = []) =>
  lines
    .map((line, index) => ({ line, index }))
    .sort(
      (a, b) =>
        getDocLineVisOrder(a.line, a.index + 1) - getDocLineVisOrder(b.line, b.index + 1) || a.index - b.index
    )
    .map(({ line }) => line);

const getNextDocLineVisOrder = (lines = []) =>
  lines.reduce((maxOrder, line, index) => Math.max(maxOrder, getDocLineVisOrder(line, index + 1)), 0) + 1;

const EditToolbar = (props) => {
  const { disabled, push, handleClick } = props;

  return (
    <GridToolbarContainer>
      <AddButtonUi disabled={disabled} onClick={() => handleClick(push)} />
    </GridToolbarContainer>
  );
};

const DocumentLine = ({
  disabled,
  defaultField,
  autocompleteOptions,
  autocompleteData,
  documentData,
  selectedCompanyState,
  viewField,
  datagridIdentifiers,
  currentDocType,
  jokerLineFields,
  selectedValues,
  batchLineState,
  binLineState,
  setNoteDialogState,
  docTypes,
  setAutoCompleteData,
  setAutoCompleteOptions,
  docTotal,
  docTypeId,
}) => {
  const gridHeight = useWindowSubtractedHeight({ otherContentHeight: 610, minHeight: 480 });

  const apiRef = useGridApiRef();
  const focusedFieldRef = useRef(null);
  const skipRecalcNetPriceRef = useRef(new Set());
  const grossDrivenLineIdsRef = useRef(new Map());
  const vatContextRef = useRef('');
  const previousCompanyIdRef = useRef(undefined);
  const { params } = useParam();
  const ameCodes = useAME(params?.USE_AME === 'Y');
  const { values, validateField, validateForm, setFieldValue, setValues, errors } = useFormikContext();
  const previousConversionRef = useRef({
    docId: values?.docId,
    currencyCode: values?.currencyCode,
    exchangeRate: values?.exchangeRate,
  });
  const currentDocLinesRef = useRef(values.docLine ?? []);
  currentDocLinesRef.current = values.docLine ?? [];
  const docLineIdentity = (values.docLine ?? [])
    .map((line) => `${line?.id ?? ''}:${line?.itemCode ?? ''}`)
    .join('|');
  const { config, getConfig } = useConfig();
  const { getUser, user } = useUsers();
  const { selectedCompany } = selectedCompanyState;
  const { setBatchLine } = batchLineState;
  const { setBinLine } = binLineState;
  const isDownPaymentDocument = DOWN_PAYMENT_DOCUMENT_TYPES.includes(Number(docTypeId));

  const [itemDialogLineId, setItemDialogLineId] = useState(null);
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [itemDialogSelectedItemCode, setItemDialogSelectedItemCode] = useState(null);
  const [itemDialogHideRightPanel, setItemDialogHideRightPanel] = useState(false);
  const [itemDialogSelectOnClose, setItemDialogSelectOnClose] = useState(true);
  const [itemGroups, setItemGroups] = useState([]);
  const [itemCompanyCatalogDialogState, setItemCompanyCatalogDialogState] = useState({
    open: false,
    rowId: null,
    rows: [],
    loading: false,
  });
  const [itemCompanyCatalogRows, setItemCompanyCatalogRows] = useState([]);

  const [itemStockDialogState, setItemStockDialogState] = useState({ open: false, data: null });
  const [prevPricesDialogState, setPrevPricesDialogState] = useState({ open: false, line: null });
  const [supportingDocumentAssignmentDialogState, setSupportingDocumentAssignmentDialogState] = useState({
    open: false,
    itemCode: null,
  });
  const [templateContextMenu, setTemplateContextMenu] = useState(null);
  const lineTextTemplates = useMemo(
    () =>
      (currentDocType?.template ?? []).filter(
        (template) =>
          template?.templateType === TemplateType.TEXT && template?.documentLevel === TemplateDocumentLevel.LINE
      ),
    [currentDocType]
  );

  const handleItemStockDialogClose = () => {
    setItemStockDialogState({ open: false, data: null });
  };

  const handlePrevPricesDialogClose = () => {
    setPrevPricesDialogState({ open: false, line: null });
  };

  const handleSupportingDocumentAssignmentDialogClose = () => {
    setSupportingDocumentAssignmentDialogState({ open: false, itemCode: null });
  };

  const supportingDocumentAssignmentEnabled =
    params?.ENABLE_SUPPORTING_DOC === 'Y' &&
    [DocumentType.CustomerOrder, DocumentType.Delivery].includes(parseInt(docTypeId));

  const handleSupportingDocumentAssignmentChange = (itemCode, supportingDocumentTypeIds) => {
    const otherAssignments = (values.supportingDocumentAssignment ?? []).filter(
      (assignment) => assignment.itemCode !== itemCode
    );
    const itemAssignments = [...new Set(supportingDocumentTypeIds)].map((supportingDocumentTypeId) => ({
      itemCode,
      supportingDocumentTypeId,
    }));

    setFieldValue('supportingDocumentAssignment', [...otherAssignments, ...itemAssignments]);
  };

  const updateSupportingDocumentAssignmentForItemChange = async (
    valueIndex,
    itemCode,
    previousItemCode,
    batchManaged
  ) => {
    if (!supportingDocumentAssignmentEnabled || itemCode === previousItemCode) return;

    const otherItemCodes = (values.docLine ?? [])
      .filter((_, index) => index !== valueIndex)
      .map((line) => line.itemCode)
      .filter(Boolean);
    let assignments = [...(values.supportingDocumentAssignment ?? [])];

    if (previousItemCode && !otherItemCodes.includes(previousItemCode)) {
      assignments = assignments.filter((assignment) => assignment.itemCode !== previousItemCode);
    }

    const hasItemAssignments = assignments.some((assignment) => assignment.itemCode === itemCode);

    if (itemCode && !otherItemCodes.includes(itemCode) && !hasItemAssignments) {
      const itemLinks = await supportingDocumentTypeService.getItemLinks(itemCode);
      const defaultAssignments = (Array.isArray(itemLinks) ? itemLinks : [])
        .filter((link) => batchManaged || link.supportingDocumentType?.generated)
        .map((link) => ({
          itemCode,
          supportingDocumentTypeId: Number(link.supportingDocumentTypeId),
        }));

      assignments = [...assignments, ...defaultAssignments];
    }

    setFieldValue('supportingDocumentAssignment', assignments);
  };

  const handleRemoveLine = (remove, valueIndex) => {
    if (valueIndex < 0) return;

    const itemCode = values.docLine?.[valueIndex]?.itemCode;
    const hasOtherItemLine = (values.docLine ?? []).some(
      (line, index) => index !== valueIndex && line.itemCode === itemCode
    );

    if (supportingDocumentAssignmentEnabled && itemCode && !hasOtherItemLine) {
      setFieldValue(
        'supportingDocumentAssignment',
        (values.supportingDocumentAssignment ?? []).filter((assignment) => assignment.itemCode !== itemCode)
      );
    }

    remove(valueIndex);
  };

  const handleOrderChange = useCallback(
    (rowId, direction) => {
      const orderedLines = getOrderedDocLines(values.docLine);
      const currentIndex = orderedLines.findIndex((line) => line.id === rowId);
      const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

      if (currentIndex < 0 || targetIndex < 0 || targetIndex >= orderedLines.length) return;

      const currentLine = orderedLines[currentIndex];
      const targetLine = orderedLines[targetIndex];
      const currentOrder = getDocLineVisOrder(currentLine, currentIndex + 1);
      const targetOrder = getDocLineVisOrder(targetLine, targetIndex + 1);

      const updatedLines = orderedLines.map((line) => {
        if (line.id === currentLine.id) return { ...line, visOrder: targetOrder };
        if (line.id === targetLine.id) return { ...line, visOrder: currentOrder };
        return line;
      });

      setFieldValue('docLine', getOrderedDocLines(updatedLines));
    },
    [setFieldValue, values.docLine]
  );

  const handleItemCompanyCatalogDialogClose = () => {
    setItemCompanyCatalogDialogState({
      open: false,
      rowId: null,
      rows: [],
      loading: false,
    });
  };

  const itemCompanyCatalogOptions = useMemo(
    () =>
      itemCompanyCatalogRows.map((row) => ({
        label: [row.itemCodeInBp, row.itemNameInBp, row.itemCode].filter(Boolean).join(' - '),
        value: row.id,
      })),
    [itemCompanyCatalogRows]
  );

  const [selectedDocCur, setSelectedDocCur] = useState(null);
  const [selectedOwnCur, setSelectedOwnCur] = useState(null);
  const [docRounding, setDocRounding] = useState(0);
  const [projectPriceReloadState, setProjectPriceReloadState] = useState({
    open: false,
    lineIndexes: [],
  });
  const previousLineProjectsRef = useRef([]);

  const documentLineGridSx = useDocumentLineGridSx();

  useEffect(() => {
    const docCur = autocompleteData?.currency?.find((c) => c.code === values.currencyCode);
    setSelectedDocCur(docCur);
    setDocRounding(getRounding(documentData.direction, docCur));
  }, [values.currencyCode, autocompleteData.currency, documentData.direction]);

  useEffect(() => {
    const ownCur = autocompleteData?.currency?.find((c) => c.code === config?.currencyCode);
    setSelectedOwnCur(ownCur);
  }, [autocompleteData.currency, config?.currencyCode, documentData.direction]);

  useEffect(() => {
    //docTotal(values, setFieldValue);
    const previousConversion = previousConversionRef.current;
    const documentChanged = previousConversion.docId !== values.docId;
    const conversionChanged =
      !documentChanged &&
      (previousConversion.currencyCode !== values.currencyCode || previousConversion.exchangeRate !== values.exchangeRate);

    recalculateAllLineFields(conversionChanged);
    previousConversionRef.current = {
      docId: values.docId,
      currencyCode: values.currencyCode,
      exchangeRate: values.exchangeRate,
    };
  }, [values.docLine, values.exchangeRate, values.currencyCode, values.docId]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      docTotal(values, setFieldValue);
    }, 120);

    return () => clearTimeout(timeoutId);
  }, [values.docLine, values.exchangeRate, values.roundingInDocCur, values.currencyCode, docTotal, setFieldValue]);

  useEffect(() => {
    if (!Object.keys(config).length) getConfig();
  }, [getConfig, config]);

  useEffect(() => {
    if (!Object.keys(user ?? {}).length) {
      getUser();
    }
  }, [getUser, user]);

  useEffect(() => {
    masterDataService.itemGroupToForm().then((data) => {
      setItemGroups(Array.isArray(data) ? data : []);
    });
  }, []);

  const normalizeSingleItemResponse = (response) => {
    if (Array.isArray(response?.body)) {
      return response.body[0] ?? null;
    }

    if (Array.isArray(response)) {
      return response[0] ?? null;
    }

    return response?.body ?? response ?? null;
  };

  const ensureItemInAutocompleteCache = (item) => {
    if (!item?.itemCode) return;

    setAutoCompleteData((prev) => {
      const prevItems = Array.isArray(prev?.item) ? prev.item : [];
      if (prevItems.some((i) => i.itemCode === item.itemCode)) {
        return prev;
      }

      return {
        ...prev,
        item: [...prevItems, item],
      };
    });

    setAutoCompleteOptions((prev) => {
      const prevItems = Array.isArray(prev?.item) ? prev.item : [];
      if (prevItems.some((i) => i.value === item.itemCode)) {
        return prev;
      }

      return {
        ...prev,
        item: [
          ...prevItems,
          { label: [item.itemCode, item.itemName].filter(Boolean).join(' - '), value: item.itemCode },
        ],
      };
    });
  };

  const getItemMetaByCode = async (itemCode) => {
    if (!itemCode) return null;

    try {
      const response = await masterDataService.itemsFromTableToFormByItemCode(itemCode);
      const item = normalizeSingleItemResponse(response);
      if (item) {
        ensureItemInAutocompleteCache(item);
      }
      return item;
    } catch {
      return null;
    }
  };

  const focusFirstEditableCell = (rowId) => {
    const editableFields = getLineEditableFields(apiRef, rowId, { currentDocType, selectedValues });
    const fieldToFocus = editableFields[0] ?? 'itemCode';

    focusedFieldRef.current = fieldToFocus;
    apiRef.current.startRowEditMode({ id: rowId });
    apiRef.current.setCellFocus(rowId, fieldToFocus);
    focusEditorInput(rowId, fieldToFocus);
  };

  const createNewLineData = async () => {
    const findedProject = autocompleteData?.project.find((p) => p.projectId === values.projectId);
    const findedItem = await getItemMetaByCode(defaultField.itemCode);
    const findedQtyUnit = autocompleteData.qtyUnit.find((u) => u.id === findedItem?.quantityUnitId);

    let vatObj = { vatCode: null, vatPercent: 0 };

    if (findedItem?.itemCode) {
      // New line default item should use the same service-based VAT resolution as item changes.
      vatObj = await resolveVatForItem(findedItem.itemCode, findedItem);
    } else {
      const resolvedVatCode = selectedCompany
        ? documentData.direction === DocumentDirection.INCOMING
          ? selectedCompany?.supplierVatCode
          : selectedCompany?.customerVatCode
        : null;

      const findedVat = autocompleteData.vatCode.find((u) => u.code === resolvedVatCode);

      vatObj = {
        vatCode: resolvedVatCode,
        vatPercent: findedVat?.percentage ?? 0,
      };
    }

    return {
      ...defaultField,
      id: uuidv4(),
      isNew: true,
      visOrder: getNextDocLineVisOrder(values.docLine),
      dueDate: values.dueDate ?? defaultField.dueDate,
      vatCode: vatObj.vatCode,
      vatPercent: vatObj.vatPercent,
      projectId: values.projectId,
      projectOrderNumber: findedProject?.orderNumber ?? '',
      costCenterCode: values.costCenterCode,
      whsCode: values.whsCode ?? defaultField.whsCode,
      itemName: findedItem ? findedItem.itemName : '',
      drawingNumber: findedItem?.drawingNumber ?? null,
      weightChangeNumber: findedItem ? findedItem.weightChangeNumber : null,
      quantityUnitId: findedItem ? findedItem.quantityUnitId : '',
      quantityUnitCode: findedQtyUnit ? findedQtyUnit.code : '',
      quantityUnitName: findedQtyUnit ? findedQtyUnit.name : '',
      landedCost: false,
      ameCode: params?.USE_AME === 'Y' ? findedItem?.ameCode ?? null : null,
    };
  };

  const validateDocumentDatesBeforeLineAdd = () => {
    if (values.docDate && values.issueDate && values.dueDate) return true;

    addNotification({
      content: 'A tételsor rögzítése előtt adja meg a bizonylat dátumait, mert azok befolyásolják a sor határidejét és az árfolyamot!',
      type: NotificationType.WARNING,
    });

    return false;
  };

  const handleClick = async (push) => {
    if (!validateDocumentDatesBeforeLineAdd()) return;

    const newField = await createNewLineData();
    const newIndex = values.docLine.length ?? 0;

    push(newField);

    if (newField.itemCode) {
      const defaultItem = await getItemMetaByCode(newField.itemCode);
      await updateSupportingDocumentAssignmentForItemChange(
        newIndex,
        newField.itemCode,
        null,
        defaultItem?.invManagementMethod === InvManagementMethod.BATCH
      );
    }

    let attempts = 0;
    const focusWhenReady = () => {
      const row = apiRef.current.getRow?.(newField.id);
      if (row) {
        focusFirstEditableCell(newField.id);
        return;
      }

      if (attempts < 8) {
        attempts += 1;
        setTimeout(focusWhenReady, 0);
      }
    };

    setTimeout(focusWhenReady, 0);

    // Wait one tick so Formik contains the pushed row before validation runs.
    setTimeout(() => {
      if (values.docLine?.[newIndex] || apiRef.current.getRow?.(newField.id)) {
        validateForm();
      }
    }, 0);

    return defaultField.id;
  };

  const getSelectedAddressCountryCode = () => {
    const companyAddresses = selectedCompany?.companyAdress ?? [];

    const shippingAddress = companyAddresses.find((address) => address.id === values.shippingAdressId);
    if (shippingAddress?.countryCode) return shippingAddress.countryCode;

    const billingAddress = companyAddresses.find((address) => address.id === values.billingAdressId);
    if (billingAddress?.countryCode) return billingAddress.countryCode;

    return undefined;
  };

  const getAreaByCountryCode = (countryCode) => {
    if (!countryCode) return undefined;

    if (config?.countryCode && config.countryCode === countryCode) {
      return AreaType.BE;
    }

    const matchedCountry = autocompleteData.country?.find((country) => country.code === countryCode);
    if (!matchedCountry) return undefined;

    return matchedCountry.euMember === true ? AreaType.EU : AreaType.EK;
  };

  const getDefaultVatCodeForItem = (selectedItem) => {
    const { purchaseVatCodeService, purchaseVatCodeItem, salesVatCodeService, salesVatCodeItem } = config;

    return documentData.direction === DocumentDirection.INCOMING
      ? selectedItem.itemType === ItemGroupType.SERVICE
        ? purchaseVatCodeService
        : purchaseVatCodeItem
      : selectedItem.itemType === ItemGroupType.SERVICE
        ? salesVatCodeService
        : salesVatCodeItem;
  };

  const resolveVatForItem = async (itemCode, selectedItem) => {
    const defaultVatCode = getDefaultVatCodeForItem(selectedItem);
    let usedVat = autocompleteData.vatCode.find((vat) => vat.code === defaultVatCode);

    const countryCode = getSelectedAddressCountryCode();
    const area = getAreaByCountryCode(countryCode);

    const resolvedVatCode = await vatCodeService.getVatCodeToDoc({
      documentTypeId: documentData.documentTypeId,
      itemCode,
      companyId: values.companyId ?? null,
      area,
    });

    if (resolvedVatCode) {
      usedVat = autocompleteData.vatCode.find((vat) => vat.code === resolvedVatCode) ?? usedVat;
    }

    return usedVat ? { vatCode: usedVat.code, vatPercent: usedVat.percentage } : { vatCode: null, vatPercent: null };
  };

  const applyVatToLine = async (valueIndex, itemCode, selectedItemParam = null) => {
    const currentLine = values.docLine?.[valueIndex];
    if (isDownPaymentApplicationLine(currentLine)) {
      return { vatCode: currentLine.vatCode, vatPercent: currentLine.vatPercent };
    }

    const selectedItem = selectedItemParam ?? (await getItemMetaByCode(itemCode));
    if (!selectedItem) return { vatCode: null, vatPercent: null };

    const vatObj = await resolveVatForItem(itemCode, selectedItem);

    setFieldValue(`docLine.${valueIndex}.vatCode`, vatObj.vatCode);
    setFieldValue(`docLine.${valueIndex}.vatPercent`, vatObj.vatPercent);

    apiRef.current?.setEditCellValue({
      id: values.docLine[valueIndex].id,
      field: 'vatCode',
      value: vatObj.vatCode,
    });

    apiRef.current?.setEditCellValue({
      id: values.docLine[valueIndex].id,
      field: 'vatPercent',
      value: vatObj.vatPercent,
    });

    if (isGrossDrivenCalculation(valueIndex)) {
      calculateGrossDrivenPrice(
        valueIndex,
        values.docLine[valueIndex].qty,
        values.docLine[valueIndex].grossUnitPrice,
        vatObj.vatPercent ?? 0
      );
    } else {
      calculatePrice(
        valueIndex,
        values.docLine[valueIndex].qty,
        values.docLine[valueIndex].unitPrice,
        vatObj.vatPercent ?? 0
      );
    }

    if (!isGrossDrivenCalculation(valueIndex)) {
      const grossUnitPrice = round(
        calculateGrossPrice(values.docLine[valueIndex].unitPrice ?? 0, vatObj.vatPercent ?? 0),
        docRounding
      );

      setFieldValue(`docLine.${valueIndex}.grossUnitPrice`, grossUnitPrice);
      setFieldValue(`docLine.${valueIndex}.baseGrossUnitPrice`, grossUnitPrice);
    }

    return vatObj;
  };

  const getResolvedItemPrice = async (itemCode, projectId = null) => {
    if (!itemCode || !values.priceListId) return null;

    const findedPriceList = autocompleteData.priceList.find((priceList) => priceList.id === values.priceListId);
    const priceListPrice = await priceListService.getItemPrice({
      itemCode,
      priceListId: values.priceListId,
      projectId,
      docDate: values.docDate,
    });

    if (!priceListPrice?.price) return null;

    return {
      price: Number(priceListPrice.price),
      source: priceListPrice.source ?? 'PRICELIST',
      priceForm: findedPriceList?.priceForm ?? PriceFormField.NET,
    };
  };

  const resolveLinePrice = async ({ itemCode, projectId, targetDate }) => {
    if (!targetDate) return null;
    return getResolvedItemPrice(itemCode, projectId ?? null);
  };

  const applyResolvedPriceToLine = async (valueIndex, resolvedPrice, vatPercent) => {
    if (!resolvedPrice || !Number.isFinite(Number(resolvedPrice.price))) {
      return { price: null, source: null };
    }

    const numericPrice = Number(resolvedPrice.price);

    setFieldValue(`docLine.${valueIndex}.discountPercent`, null);
    setFieldValue(`docLine.${valueIndex}.ruleId`, null);

    if (resolvedPrice.source === 'PROJECT' || resolvedPrice.priceForm !== PriceFormField.GROSS) {
      setGrossDrivenCalculation(valueIndex, false);
      setFieldValue(`docLine.${valueIndex}.unitPrice`, numericPrice);
      setFieldValue(`docLine.${valueIndex}.baseUnitPrice`, numericPrice);
      calculatePrice(valueIndex, values.docLine[valueIndex].qty, numericPrice, vatPercent ?? 0);

      const grossUnitPrice = round(calculateGrossPrice(numericPrice, vatPercent ?? 0), docRounding);
      setFieldValue(`docLine.${valueIndex}.grossUnitPrice`, grossUnitPrice);
      setFieldValue(`docLine.${valueIndex}.baseGrossUnitPrice`, grossUnitPrice);

      return { price: numericPrice, source: resolvedPrice.source, grossDriven: false };
    }

    setGrossDrivenCalculation(valueIndex, true);
    const netPrice = handleGrossPriceChange(valueIndex, numericPrice, vatPercent ?? 0);
    setFieldValue(`docLine.${valueIndex}.baseUnitPrice`, netPrice);
    setFieldValue(`docLine.${valueIndex}.grossUnitPrice`, numericPrice);
    setFieldValue(`docLine.${valueIndex}.baseGrossUnitPrice`, numericPrice);

    return { price: numericPrice, source: resolvedPrice.source, grossDriven: true };
  };

  const refreshLinePrice = async (valueIndex, options = {}) => {
    const line = values.docLine?.[valueIndex];
    const resolvedItemCode = options.itemCode ?? line?.itemCode ?? null;
    if (!resolvedItemCode) return false;

    const lineProjectId = options.projectId ?? line?.projectId ?? values.projectId ?? null;
    const resolvedVatPercent = options.vatPercent ?? line.vatPercent ?? 0;
    const resolvedPrice = await resolveLinePrice({
      itemCode: resolvedItemCode,
      projectId: lineProjectId,
      targetDate: values.docDate,
    });

    if (!resolvedPrice) return false;

    const appliedPrice = await applyResolvedPriceToLine(valueIndex, resolvedPrice, resolvedVatPercent);

    if (resolvedPrice.source === 'PRICELIST' && options.applyDiscount !== false) {
      const discount = await discountRuleService.getDiscount({
        itemCode: resolvedItemCode,
        companyId: values.companyId ?? null,
        priceListId: values.priceListId ?? null,
        date: values.docDate,
      });

      if (discount?.percent && discount?.id && appliedPrice.price !== null) {
        setFieldValue(`docLine.${valueIndex}.discountPercent`, discount.percent);
        setFieldValue(`docLine.${valueIndex}.ruleId`, discount.id);

        const discountedUnitPrice = calculateDiscountValue(discount.percent, appliedPrice.price);
        if (appliedPrice.grossDriven) {
          setFieldValue(`docLine.${valueIndex}.grossUnitPrice`, discountedUnitPrice);
          setFieldValue(
            `docLine.${valueIndex}.unitPrice`,
            round(calculateNetPrice(discountedUnitPrice, resolvedVatPercent), docRounding)
          );
          calculateGrossDrivenPrice(valueIndex, line.qty, discountedUnitPrice, resolvedVatPercent);
        } else {
          setFieldValue(`docLine.${valueIndex}.unitPrice`, discountedUnitPrice);
          calculatePrice(valueIndex, line.qty, discountedUnitPrice, resolvedVatPercent);
        }
      }
    }

    return resolvedPrice.source === 'PROJECT';
  };

  const handleItemChange = async (
    valueIndex,
    value,
    fromPaste = false,
    selectedPartnerItem = null,
    selectedItemParam = null
  ) => {
    const previousItemCode = values.docLine?.[valueIndex]?.itemCode;
    const selectedItem = selectedItemParam ?? (await getItemMetaByCode(value));

    setValues((previousValues) => {
      const docLine = [...(previousValues.docLine ?? [])];
      docLine[valueIndex] = {
        ...docLine[valueIndex],
        itemCode: value,
        itemName: selectedItem?.itemName ?? null,
      };

      return {
        ...previousValues,
        docLine,
      };
    }, false);

    await updateSupportingDocumentAssignmentForItemChange(
      valueIndex,
      selectedItem?.itemCode ?? null,
      previousItemCode,
      selectedItem?.invManagementMethod === InvManagementMethod.BATCH
    );
    if (selectedItem) {
      // item fő adatok
      setFieldValue(`docLine.${valueIndex}.drawingNumber`, selectedItem?.drawingNumber ?? null);
      setFieldValue(`docLine.${valueIndex}.quantityUnitId`, selectedItem?.quantityUnitId ?? null);
      const findedQtyUnit = autocompleteData.qtyUnit.find((u) => u.id === selectedItem?.quantityUnitId);
      setFieldValue(`docLine.${valueIndex}.quantityUnitCode`, findedQtyUnit?.code ?? null);
      setFieldValue(`docLine.${valueIndex}.quantityUnitName`, findedQtyUnit?.name ?? null);
      setFieldValue(`docLine.${valueIndex}.weightChangeNumber`, selectedItem?.weight ?? null);
      setFieldValue(`docLine.${valueIndex}.landedCost`, Boolean(selectedItem?.landedCost));
      setFieldValue(`docLine.${valueIndex}.ameCode`, params?.USE_AME === 'Y' ? selectedItem?.ameCode ?? null : null);
      setFieldValue(`docLine.${valueIndex}.itemCodeInBp`, null);
      setFieldValue(`docLine.${valueIndex}.itemNameInBp`, null);

      calculateWeight(valueIndex, values.docLine[valueIndex].qty, selectedItem?.weight ?? null);
      const vatObj = await applyVatToLine(valueIndex, value, selectedItem);
      if (!fromPaste) {
        const lineProjectId = values.docLine?.[valueIndex]?.projectId ?? values.projectId ?? null;
        const resolvedPrice = await getResolvedItemPrice(value, lineProjectId);

        if (resolvedPrice) {
          const appliedPrice = await applyResolvedPriceToLine(valueIndex, resolvedPrice, vatObj.vatPercent);

          if (resolvedPrice.source === 'PRICELIST') {
            const discount = await discountRuleService.getDiscount({
              itemCode: value,
              companyId: values.companyId ?? null,
              priceListId: values.priceListId ?? null,
              date: values.docDate,
            });

            if (discount?.percent && discount?.id && appliedPrice.price !== null) {
              setFieldValue(`docLine.${valueIndex}.discountPercent`, discount.percent);
              setFieldValue(`docLine.${valueIndex}.ruleId`, discount.id);

              const discountedUnitPrice = calculateDiscountValue(discount.percent, appliedPrice.price);
              if (appliedPrice.grossDriven) {
                setFieldValue(`docLine.${valueIndex}.grossUnitPrice`, discountedUnitPrice);
                setFieldValue(
                  `docLine.${valueIndex}.unitPrice`,
                  round(calculateNetPrice(discountedUnitPrice, vatObj.vatPercent ?? 0), docRounding)
                );
                calculateGrossDrivenPrice(
                  valueIndex,
                  values.docLine[valueIndex].qty,
                  discountedUnitPrice,
                  vatObj.vatPercent ?? 0
                );
              } else {
                setFieldValue(`docLine.${valueIndex}.unitPrice`, discountedUnitPrice);
                calculatePrice(
                  valueIndex,
                  values.docLine[valueIndex].qty,
                  discountedUnitPrice,
                  vatObj.vatPercent ?? 0
                );
              }
            }
          }
        }
      }

      if (selectedPartnerItem) {
        setFieldValue(`docLine.${valueIndex}.itemCodeInBp`, selectedPartnerItem?.itemCodeInBp ?? null);
        setFieldValue(`docLine.${valueIndex}.itemNameInBp`, selectedPartnerItem?.itemNameInBp ?? null);
      } else if (values.companyId) {
        const companyCatalogRows = await itemCompanyCatalogService.getActiveItemsByCompany(values.companyId, value);
        const firstPartnerItem = companyCatalogRows[0] ?? null;

        setFieldValue(`docLine.${valueIndex}.itemCodeInBp`, firstPartnerItem?.itemCodeInBp ?? null);
        setFieldValue(`docLine.${valueIndex}.itemNameInBp`, firstPartnerItem?.itemNameInBp ?? null);
      }
    } else {
      setFieldValue(`docLine.${valueIndex}.drawingNumber`, null);
      setFieldValue(`docLine.${valueIndex}.quantityUnitId`, null);
      setFieldValue(`docLine.${valueIndex}.quantityUnitCode`, null);
      setFieldValue(`docLine.${valueIndex}.quantityUnitName`, null);
      setFieldValue(`docLine.${valueIndex}.weightChangeNumber`, null);
      setFieldValue(`docLine.${valueIndex}.unitPrice`, null);
      setFieldValue(`docLine.${valueIndex}.baseUnitPrice`, null);
      setFieldValue(`docLine.${valueIndex}.landedCost`, false);
      setFieldValue(`docLine.${valueIndex}.ameCode`, null);
      setFieldValue(`docLine.${valueIndex}.itemCodeInBp`, null);
      setFieldValue(`docLine.${valueIndex}.itemNameInBp`, null);
    }
  };

  const loadItemCompanyCatalogRows = async (companyId) => {
    const rows = await itemCompanyCatalogService.getActiveItemsByCompany(companyId);

    return rows.map((row) => {
      const matchedItem = autocompleteData.item.find((item) => item.itemCode === row.itemCode);

      return {
        ...row,
        ownItemName: row.itemName ?? row.item?.itemName ?? matchedItem?.itemName ?? '',
      };
    });
  };

  useEffect(() => {
    if (!values.companyId) {
      setItemCompanyCatalogRows([]);
      return;
    }

    loadItemCompanyCatalogRows(values.companyId)
      .then((rows) => {
        setItemCompanyCatalogRows(rows);
      })
      .catch(() => {
        setItemCompanyCatalogRows([]);
      });
  }, [values.companyId, autocompleteData.item]);

  useEffect(() => {
    const previousCompanyId = previousCompanyIdRef.current;
    const currentCompanyId = values.companyId ?? null;
    const validDocLines = (values.docLine ?? []).filter((line) => line?.id != null);

    if (previousCompanyId !== undefined && previousCompanyId !== currentCompanyId) {
      setFieldValue(
        'docLine',
        validDocLines.map((line) => ({
          ...line,
          itemCodeInBp: null,
          itemNameInBp: null,
        }))
      );
    } else if (validDocLines.length !== (values.docLine ?? []).length) {
      setFieldValue('docLine', validDocLines);
    }

    previousCompanyIdRef.current = currentCompanyId;

    if (!currentCompanyId) return;

    let isCurrent = true;

    const fillPartnerItemCodes = async () => {
      const partnerItemCodes = await Promise.all(
        validDocLines.map(async (line) => {
          if (!line.itemCode) return null;

          const companyCatalogRows = await itemCompanyCatalogService
            .getActiveItemsByCompany(currentCompanyId, line.itemCode)
            .catch(() => []);

          return {
            id: line.id,
            itemCode: line.itemCode,
            itemCodeInBp: companyCatalogRows[0]?.itemCodeInBp ?? null,
            itemNameInBp: companyCatalogRows[0]?.itemNameInBp ?? null,
          };
        })
      );

      if (!isCurrent || previousCompanyIdRef.current !== currentCompanyId) return;

      const partnerItemCodeByLineId = new Map(
        partnerItemCodes.filter(Boolean).map((partnerItemCode) => [partnerItemCode.id, partnerItemCode])
      );
      const currentDocLines = currentDocLinesRef.current.filter((line) => line?.id != null);
      const updatedDocLines = currentDocLines.map((line) => {
        const partnerItemCode = partnerItemCodeByLineId.get(line.id);
        if (!partnerItemCode || partnerItemCode.itemCode !== line.itemCode) return line;

        return {
          ...line,
          itemCodeInBp: partnerItemCode.itemCodeInBp,
          itemNameInBp: partnerItemCode.itemNameInBp,
        };
      });

      setFieldValue('docLine', updatedDocLines);
    };

    fillPartnerItemCodes();

    return () => {
      isCurrent = false;
    };
  }, [values.companyId, docLineIdentity]);

  const handleItemCompanyCatalogDialogOpen = async (rowId) => {
    if (!values.companyId) {
      addNotification({
        content: 'Előbb válassz üzleti partnert!',
        type: NotificationType.WARNING,
      });
      return;
    }

    setItemCompanyCatalogDialogState({
      open: true,
      rowId,
      rows: [],
      loading: true,
    });

    try {
      const rows = await loadItemCompanyCatalogRows(values.companyId);
      setItemCompanyCatalogDialogState({
        open: true,
        rowId,
        rows,
        loading: false,
      });
    } catch {
      addNotification({
        content: 'Hiba történt az ÜP cikkszámok lekérdezésekor!',
        type: NotificationType.ERROR,
      });
      handleItemCompanyCatalogDialogClose();
    }
  };

  const selectItemCompanyCatalogRow = async (row) => {
    const rowId = itemCompanyCatalogDialogState.rowId;
    handleItemCompanyCatalogDialogClose();

    if (!row?.itemCode || rowId == null) {
      return;
    }

    const index = getValueIndex(values, rowId);
    if (index === -1 || !values.docLine?.[index]) return;

    const selectedItem = await getItemMetaByCode(row.itemCode);
    if (!selectedItem) {
      addNotification({
        content: 'Hiba történt a cikk adatainak lekérésekor!',
        type: NotificationType.ERROR,
      });
      return;
    }

    await handleItemChange(index, row.itemCode, false, row);

    const currentQty = values.docLine?.[index]?.qty;
    if (!currentQty || Number(currentQty) === 0) {
      handleQtyChange(index, 1);
    }
  };

  const refreshLineVatCodes = async () => {
    for (let index = 0; index < values.docLine.length; index += 1) {
      const line = values.docLine[index];

      if (!line?.itemCode || !line.isNew) continue;

      await applyVatToLine(index, line.itemCode);
    }
  };

  useEffect(() => {
    if (!values.docLine?.length || !autocompleteData.vatCode?.length) {
      vatContextRef.current = '';
      return;
    }

    const vatContextKey = JSON.stringify({
      documentTypeId: documentData.documentTypeId ?? null,
      companyId: values.companyId ?? null,
      selectedCompanyId: selectedCompany?.id ?? null,
      shippingAdressId: values.shippingAdressId ?? null,
      billingAdressId: values.billingAdressId ?? null,
      ownCountryCode: config?.countryCode ?? null,
      countriesLoaded: autocompleteData.country?.length ?? 0,
    });

    if (vatContextRef.current === vatContextKey) return;
    vatContextRef.current = vatContextKey;

    refreshLineVatCodes();
  }, [
    documentData.documentTypeId,
    values.companyId,
    values.docLine?.length,
    values.shippingAdressId,
    values.billingAdressId,
    selectedCompany?.id,
    autocompleteData.country,
    autocompleteData.vatCode,
    config?.countryCode,
  ]);

  useEffect(() => {
    const currentLineProjects = (values.docLine ?? []).map((line) => ({
      id: line.id,
      itemCode: line.itemCode ?? null,
      projectId: line.projectId ?? null,
    }));

    if (previousLineProjectsRef.current.length === 0) {
      previousLineProjectsRef.current = currentLineProjects;
      return;
    }

    const previousProjectsById = new Map(
      previousLineProjectsRef.current.map((line) => [line.id, line.projectId ?? null])
    );
    previousLineProjectsRef.current = currentLineProjects;

    if (viewField || currentLineProjects.length === 0) {
      return;
    }

    const changedLineIndexes = currentLineProjects.reduce((acc, line, index) => {
      const previousProjectId = previousProjectsById.get(line.id);

      if (
        previousProjectId !== undefined &&
        previousProjectId !== (line.projectId ?? null) &&
        line.itemCode &&
        line.projectId
      ) {
        acc.push(index);
      }

      return acc;
    }, []);

    if (changedLineIndexes.length === 0) {
      return;
    }

    let isMounted = true;

    const checkProjectPrices = async () => {
      const lineIndexesWithProjectPrice = [];

      for (const index of changedLineIndexes) {
        const line = values.docLine?.[index];
        const resolvedProjectPrice = await getResolvedItemPrice(line?.itemCode, line?.projectId ?? null);
        const resolvedDefaultPrice = await getResolvedItemPrice(line?.itemCode, null);

        const hasProjectSpecificResolvedPrice =
          resolvedProjectPrice &&
          Number.isFinite(Number(resolvedProjectPrice.price)) &&
          (resolvedProjectPrice.source === 'PROJECT' ||
            Number(resolvedProjectPrice.price) !== Number(resolvedDefaultPrice?.price));

        if (hasProjectSpecificResolvedPrice) {
          lineIndexesWithProjectPrice.push(index);
        }
      }

      if (isMounted && lineIndexesWithProjectPrice.length > 0) {
        setProjectPriceReloadState({
          open: true,
          lineIndexes: lineIndexesWithProjectPrice,
        });
      }
    };

    checkProjectPrices();

    return () => {
      isMounted = false;
    };
  }, [values.docLine, values.docDate, viewField]);

  const calculateGrossPrice = (netPrice, vatPercent) => {
    return multiply(netPrice ?? 0, add(1, divide(vatPercent, 100)));
  };

  const calculateNetPrice = (grossPrice, vatPercent) => {
    return divide(multiply(grossPrice ?? 0, 100), add(100, vatPercent));
  };

  const setGrossDrivenCalculation = (valueIndex, grossDriven) => {
    const lineId = values.docLine?.[valueIndex]?.id;
    if (!lineId) return;

    grossDrivenLineIdsRef.current.set(lineId, grossDriven);
  };

  const isGrossDrivenCalculation = (valueIndex) => {
    const lineId = values.docLine?.[valueIndex]?.id;
    if (lineId && grossDrivenLineIdsRef.current.has(lineId)) {
      return grossDrivenLineIdsRef.current.get(lineId);
    }

    const selectedPriceList = autocompleteData?.priceList?.find((priceList) => priceList.id === values.priceListId);
    return selectedPriceList?.priceForm === PriceFormField.GROSS;
  };

  const handlePriceChange = (valueIndex, value, preserveInput = false) => {
    setGrossDrivenCalculation(valueIndex, false);
    const roundedValue = round(parseFloat(value ?? 0), docRounding);
    setFieldValue(`docLine.${valueIndex}.unitPrice`, preserveInput ? value : (roundedValue ?? ''));

    setFieldValue(`docLine.${valueIndex}.discountPercent`, '');
    setFieldValue(`docLine.${valueIndex}.ruleId`, null);

    setFieldValue(`docLine.${valueIndex}.baseUnitPrice`, roundedValue);

    const vatPercent = values.docLine[valueIndex].vatPercent ?? 0;

    calculatePrice(valueIndex, values.docLine[valueIndex].qty, roundedValue ?? '0', vatPercent);

    const grossUnitPrice = round(calculateGrossPrice(roundedValue ?? 0, vatPercent), docRounding);

    setFieldValue(`docLine.${valueIndex}.grossUnitPrice`, grossUnitPrice);
    setFieldValue(`docLine.${valueIndex}.baseGrossUnitPrice`, grossUnitPrice);

    apiRef.current.setEditCellValue({
      id: values.docLine[valueIndex].id,
      field: 'discountPercent',
      value: null,
    });

    apiRef.current.setEditCellValue({
      id: values.docLine[valueIndex].id,
      field: 'baseGrossUnitPrice',
      value: grossUnitPrice,
    });
  };

  const handleGrossPriceChange = (valueIndex, value, vatPercent) => {
    setGrossDrivenCalculation(valueIndex, true);
    const roundedValue = round(parseFloat(value ?? 0), docRounding);
    setFieldValue(`docLine.${valueIndex}.grossUnitPrice`, roundedValue ?? '');
    setFieldValue(`docLine.${valueIndex}.baseGrossUnitPrice`, roundedValue ?? '');

    const netUnitPrice = round(calculateNetPrice(roundedValue ?? 0, vatPercent), docRounding);
    setFieldValue(`docLine.${valueIndex}.unitPrice`, netUnitPrice);
    setFieldValue(`docLine.${valueIndex}.baseUnitPrice`, netUnitPrice);

    setFieldValue(`docLine.${valueIndex}.discountPercent`, null);
    setFieldValue(`docLine.${valueIndex}.ruleId`, null);

    apiRef.current.setEditCellValue({
      id: values.docLine[valueIndex].id,
      field: 'baseGrossUnitPrice',
      value: roundedValue,
    });

    apiRef.current.setEditCellValue({
      id: values.docLine[valueIndex].id,
      field: 'discountPercent',
      value: null,
    });

    apiRef.current.setEditCellValue({
      id: values.docLine[valueIndex].id,
      field: 'baseUnitPrice',
      value: netUnitPrice,
    });

    calculateGrossDrivenPrice(valueIndex, values.docLine[valueIndex].qty, roundedValue, vatPercent);

    return netUnitPrice;
  };

  const handleNetPriceChange = (valueIndex, value) => {
    setGrossDrivenCalculation(valueIndex, false);
    const roundedNetPrice = round(parseFloat(value ?? 0), selectedDocCur?.amountRounding ?? 0);
    const qty = parseFloat(values.docLine[valueIndex].qty ?? '0');
    const vatPercent = values.docLine[valueIndex].vatPercent ?? 0;

    if (values.docLine[valueIndex]?.id) {
      skipRecalcNetPriceRef.current.add(values.docLine[valueIndex].id);
    }

    setFieldValue(`docLine.${valueIndex}.netPrice`, roundedNetPrice ?? '');

    const netUnitPrice = qty > 0 ? round(divide(roundedNetPrice ?? 0, qty), docRounding) : 0;
    setFieldValue(`docLine.${valueIndex}.unitPrice`, netUnitPrice);
    setFieldValue(`docLine.${valueIndex}.baseUnitPrice`, netUnitPrice);

    setFieldValue(`docLine.${valueIndex}.discountPercent`, null);
    setFieldValue(`docLine.${valueIndex}.ruleId`, null);

    const grossUnitPrice = round(calculateGrossPrice(netUnitPrice ?? 0, vatPercent), docRounding);
    setFieldValue(`docLine.${valueIndex}.grossUnitPrice`, grossUnitPrice);
    setFieldValue(`docLine.${valueIndex}.baseGrossUnitPrice`, grossUnitPrice);

    const vatValue =
      vatPercent > 0
        ? round(multiply(roundedNetPrice, divide(vatPercent, 100)), selectedDocCur?.amountRounding ?? 0)
        : 0;
    const grossPrice = round(add(roundedNetPrice, vatValue), docRounding);

    setFieldValue(`docLine.${valueIndex}.vatValue`, vatValue);
    setFieldValue(`docLine.${valueIndex}.grossPrice`, grossPrice);

    const exchangeRate = Number(values.exchangeRate);
    const netPriceInOwnCur = round(
      multiply(roundedNetPrice, exchangeRate > 0 ? exchangeRate : 1),
      selectedOwnCur?.amountRounding ?? 0
    );
    const vatValueInOwnCur = round(
      multiply(vatValue, exchangeRate > 0 ? exchangeRate : 1),
      selectedOwnCur?.amountRounding ?? 0
    );
    const grossPriceInOwnCur = round(
      multiply(grossPrice, exchangeRate > 0 ? exchangeRate : 1),
      selectedOwnCur?.amountRounding ?? 0
    );

    setFieldValue(`docLine.${valueIndex}.netPriceInOwnCur`, netPriceInOwnCur);
    setFieldValue(`docLine.${valueIndex}.vatValueInOwnCur`, vatValueInOwnCur);
    setFieldValue(`docLine.${valueIndex}.grossPriceInOwnCur`, grossPriceInOwnCur);

    apiRef.current.setEditCellValue({
      id: values.docLine[valueIndex].id,
      field: 'netPrice',
      value: roundedNetPrice,
    });
  };

  const handleQtyChange = (valueIndex, value) => {
    const normalizedValue = normalizeNumberInput(value);
    const inputValue = parseLocaleNumber(value);
    const cplQty = parseFloat(values.docLine?.[valueIndex]?.cplQty ?? 0);
    const isNewLine = Boolean(values.docLine?.[valueIndex]?.isNew);
    const allowsNegativeQty = !currentDocType?.inventoryMovement && currentDocType?.invoice;

    if (!allowsNegativeQty && inputValue < cplQty) {
      addNotification({
        content: 'A mennyiség nem lehet kisebb a lezárt mennyiségnél!',
        type: NotificationType.ERROR,
      });

      return;
    }

    setFieldValue(`docLine.${valueIndex}.qty`, normalizedValue ?? '');

    if (isNaN(inputValue)) return;

    const rawOpnQty = subtract(inputValue, cplQty);
    const newOpnQty = rawOpnQty <= 0 ? 0 : round(rawOpnQty, 6);

    setFieldValue(`docLine.${valueIndex}.opnQty`, newOpnQty);

    // New (unsaved) lines should stay editable; don't auto-close them during typing.
    if (isNewLine) {
      setFieldValue(`docLine.${valueIndex}.lineStatus`, DocStatus.OPENED);
    } else {
      setFieldValue(`docLine.${valueIndex}.lineStatus`, newOpnQty === 0 ? DocStatus.CLOSED : DocStatus.OPENED);
    }

    if (isGrossDrivenCalculation(valueIndex)) {
      calculateGrossDrivenPrice(
        valueIndex,
        normalizedValue ?? '0',
        values.docLine[valueIndex].grossUnitPrice,
        values.docLine[valueIndex].vatPercent
      );
    } else {
      calculatePrice(
        valueIndex,
        normalizedValue ?? '0',
        values.docLine[valueIndex].unitPrice,
        values.docLine[valueIndex].vatPercent
      );
    }

    calculateWeight(valueIndex, normalizedValue ?? '0', values.docLine[valueIndex].weightChangeNumber);
  };

  const getValueIndex = (values, id) => {
    return values?.docLine?.findIndex((rc) => rc.id === id);
  };

  const isPinnedRow = (params) => params?.rowNode?.type === 'pinnedRow' || params?.id === 'pinned';

  const jokerColumns = useGenerateJokerCols(jokerLineFields, 'docLine', getValueIndex);

  const recalculateAllLineFields = (force = false) => {
    if (values?.docId && !force) return;
    values?.docLine?.forEach((line, index) => {
      const qty = line.qty ?? 0;
      const unitPrice = line.unitPrice ?? 0;
      const vatPercent = line.vatPercent ?? 0;
      const weightChangeNumber = line.weightChangeNumber ?? 0;

      if (skipRecalcNetPriceRef.current.has(line.id)) {
        skipRecalcNetPriceRef.current.delete(line.id);
      } else if (isGrossDrivenCalculation(index)) {
        calculateGrossDrivenPrice(index, qty, line.grossUnitPrice, vatPercent);
      } else {
        calculatePrice(index, qty, unitPrice, vatPercent);
      }
      calculateWeight(index, qty, weightChangeNumber);
    });
  };

  const calculateDiscountValue = (discountPercent, baseUnitPrice) => {
    const base = parseFloat(baseUnitPrice ?? 0);
    const percent = parseFloat(discountPercent ?? 0);

    if (!base) return 0;
    if (!percent) return base;

    const discountValue = multiply(base, divide(percent, 100));
    return round(subtract(base, discountValue), docRounding);
  };

  const handleDiscountPercentChange = (valueIndex, value) => {
    if (isGrossDrivenCalculation(valueIndex)) {
      const roundedValue = value ? round(parseFloat(value), 2) : null;
      const baseGrossUnitPrice =
        values.docLine[valueIndex].baseGrossUnitPrice ?? values.docLine[valueIndex].grossUnitPrice ?? 0;
      const grossUnitPrice = calculateDiscountValue(roundedValue, baseGrossUnitPrice);
      const vatPercent = values.docLine[valueIndex].vatPercent ?? 0;

      setFieldValue(`docLine.${valueIndex}.discountPercent`, roundedValue);
      setFieldValue(`docLine.${valueIndex}.ruleId`, null);
      setFieldValue(`docLine.${valueIndex}.grossUnitPrice`, grossUnitPrice);
      setFieldValue(
        `docLine.${valueIndex}.unitPrice`,
        round(calculateNetPrice(grossUnitPrice, vatPercent), docRounding)
      );
      calculateGrossDrivenPrice(valueIndex, values.docLine[valueIndex].qty, grossUnitPrice, vatPercent);
      return;
    }

    if (!value) {
      setFieldValue(`docLine.${valueIndex}.discountPercent`, null);
      setFieldValue(`docLine.${valueIndex}.ruleId`, null);
      setFieldValue(
        `docLine.${valueIndex}.unitPrice`,
        values.docLine[valueIndex].baseUnitPrice ?? values.docLine[valueIndex].unitPrice
      );

      calculatePrice(
        valueIndex,
        values.docLine[valueIndex].qty,
        values.docLine[valueIndex].baseUnitPrice,
        values.docLine[valueIndex].vatPercent
      );
    }

    const roundedValue = round(parseFloat(value ?? 0), 2);
    setFieldValue(`docLine.${valueIndex}.discountPercent`, roundedValue ?? '');
    setFieldValue(`docLine.${valueIndex}.ruleId`, null);

    const unitPrice = calculateDiscountValue(
      roundedValue,
      values.docLine[valueIndex].baseUnitPrice || values.docLine[valueIndex].unitPrice || 0
    );
    if (!values.docLine[valueIndex].baseUnitPrice) {
      setFieldValue(`docLine.${valueIndex}.baseUnitPrice`, values.docLine[valueIndex].unitPrice);
    }

    setFieldValue(`docLine.${valueIndex}.unitPrice`, unitPrice);
    calculatePrice(valueIndex, values.docLine[valueIndex].qty, unitPrice, values.docLine[valueIndex].vatPercent);
  };

  const handleItemDialogOpen = (rowId) => {
    setItemDialogSelectedItemCode(null);
    setItemDialogHideRightPanel(false);
    setItemDialogSelectOnClose(true);
    setItemDialogLineId(rowId);
    setItemDialogOpen(true);
  };

  const resetItemDialogState = () => {
    setItemDialogOpen(false);
    setItemDialogLineId(null);
    setItemDialogSelectedItemCode(null);
    setItemDialogHideRightPanel(false);
    setItemDialogSelectOnClose(true);
  };

  const hasItemViewPermission = (itemGroupId, availableItemGroups = itemGroups) => {
    if (!itemGroupId) {
      return true;
    }

    const itemGroup = availableItemGroups.find((candidate) => candidate.id === itemGroupId);
    if (!itemGroup || !Array.isArray(itemGroup.roleExcludedItemGroup)) {
      return true;
    }

    const currentUserRoleId = user?.roleId ?? null;
    if (currentUserRoleId === null) {
      return true;
    }

    return !itemGroup.roleExcludedItemGroup.some((excludedRole) => excludedRole?.roleId === currentUserRoleId);
  };

  const handleItemViewOpen = async (itemCode) => {
    if (!itemCode) return;

    const selectedItem = await getItemMetaByCode(itemCode);
    if (!selectedItem) {
      addNotification({
        content: 'Hiba történt a cikk adatainak lekérésekor!',
        type: NotificationType.ERROR,
      });
      return;
    }

    const availableItemGroups =
      itemGroups.length > 0
        ? itemGroups
        : await masterDataService.itemGroupToForm().then((data) => {
            const nextItemGroups = Array.isArray(data) ? data : [];
            setItemGroups(nextItemGroups);
            return nextItemGroups;
          });

    if (!hasItemViewPermission(selectedItem.itemGroupId, availableItemGroups)) {
      addNotification({
        content: 'Nincs jogosultsága a cikk megtekintéséhez.',
        type: NotificationType.WARNING,
      });
      return;
    }

    setItemDialogLineId(null);
    setItemDialogSelectedItemCode(itemCode);
    setItemDialogHideRightPanel(true);
    setItemDialogSelectOnClose(false);
    setItemDialogOpen(true);
  };

  const selectItemFromDialog = async (itemCode) => {
    const shouldSelectItem = itemDialogSelectOnClose;
    const currentItemDialogLineId = itemDialogLineId;
    resetItemDialogState();

    if (!shouldSelectItem || !itemCode || currentItemDialogLineId == null) {
      return;
    }

    const index = getValueIndex(values, currentItemDialogLineId);

    if (index === -1 || !values.docLine?.[index]) return;

    const selectedItem = await getItemMetaByCode(itemCode);
    if (!selectedItem) {
      addNotification({
        content: 'Hiba történt a cikk adatainak lekérésekor!',
        type: NotificationType.ERROR,
      });
      return;
    }

    await handleItemChange(index, itemCode, false, null, selectedItem);

    const currentQty = values.docLine?.[index]?.qty;
    if (!currentQty || Number(currentQty) === 0) {
      handleQtyChange(index, 1);
    }
  };

  const handleProjectPriceReloadCancel = () => {
    setProjectPriceReloadState({
      open: false,
      lineIndexes: [],
    });
  };

  const handleProjectPriceReloadConfirm = async () => {
    for (const lineIndex of projectPriceReloadState.lineIndexes) {
      await refreshLinePrice(lineIndex, { applyDiscount: false });
    }

    handleProjectPriceReloadCancel();
  };

  const pinnedLine = useMemo(() => {
    const totals = (values?.docLine ?? []).reduce(
      (acc, line) => {
        const net = parseFloat(line?.netPrice ?? 0);
        const vat = parseFloat(line?.vatValue ?? 0);
        const gross = parseFloat(line?.grossPrice ?? 0);
        const appliedGross = toAbsoluteAmount(line?.appliedGrossAmount);
        const openDownPayment = getOpenDownPaymentAmount(line?.grossPrice, line?.appliedGrossAmount);
        const weight = parseFloat(line?.weight ?? 0);
        const netOwn = parseFloat(line?.netPriceInOwnCur ?? 0);
        const vatOwn = parseFloat(line?.vatValueInOwnCur ?? 0);
        const grossOwn = parseFloat(line?.grossPriceInOwnCur ?? 0);

        return {
          netPrice: add(acc.netPrice, isNaN(net) ? 0 : net),
          vatValue: add(acc.vatValue, isNaN(vat) ? 0 : vat),
          grossPrice: add(acc.grossPrice, isNaN(gross) ? 0 : gross),
          appliedGrossAmount: add(acc.appliedGrossAmount, appliedGross),
          openDownPaymentAmount: add(acc.openDownPaymentAmount, openDownPayment),
          weight: add(acc.weight, isNaN(weight) ? 0 : weight),
          netPriceInOwnCur: add(acc.netPriceInOwnCur, isNaN(netOwn) ? 0 : netOwn),
          vatValueInOwnCur: add(acc.vatValueInOwnCur, isNaN(vatOwn) ? 0 : vatOwn),
          grossPriceInOwnCur: add(acc.grossPriceInOwnCur, isNaN(grossOwn) ? 0 : grossOwn),
        };
      },
      {
        netPrice: 0,
        vatValue: 0,
        grossPrice: 0,
        appliedGrossAmount: 0,
        openDownPaymentAmount: 0,
        weight: 0,
        netPriceInOwnCur: 0,
        vatValueInOwnCur: 0,
        grossPriceInOwnCur: 0,
      }
    );

    return {
      id: 'pinned',
      netPrice: round(totals.netPrice, docRounding),
      vatValue: round(totals.vatValue, docRounding),
      grossPrice: round(totals.grossPrice, docRounding),
      appliedGrossAmount: round(totals.appliedGrossAmount, docRounding),
      openDownPaymentAmount: round(totals.openDownPaymentAmount, docRounding),
      weight: round(totals.weight, 2),
      netPriceInOwnCur: round(totals.netPriceInOwnCur, selectedOwnCur?.amountRounding ?? 0),
      vatValueInOwnCur: round(totals.vatValueInOwnCur, selectedOwnCur?.amountRounding ?? 0),
      grossPriceInOwnCur: round(totals.grossPriceInOwnCur, selectedOwnCur?.amountRounding ?? 0),
    };
  }, [values?.docLine, docRounding]);

  const baseIsCellEditable = useMemo(
    () => isCellEditable({ currentDocType, selectedValues }),
    [currentDocType, selectedValues]
  );

  const columns = useMemo(
    () => (remove) =>
      dataGridPremiumColumns([
        {
          field: 'visOrder',
          headerName: '#',
          type: 'number',
          sortable: false,
          filterable: false,
          width: 110,
          renderCell: (params) => {
            if (isPinnedRow(params)) return '';

            const handleOrderClick = (direction) => (event) => {
              event.preventDefault();
              event.stopPropagation();
              event.defaultMuiPrevented = true;

              if (apiRef.current.getRowMode(params.id) === GridRowModes.Edit) {
                apiRef.current.stopRowEditMode({ id: params.id });
              }

              handleOrderChange(params.id, direction);
            };

            return (
              <Box display="flex" alignItems="center">
                <Box mr={1}>{getDocLineVisOrder(params.row)}</Box>
                <OrderArrowsUI
                  onUpClick={handleOrderClick('up')}
                  onDownClick={handleOrderClick('down')}
                  disabled={disabled}
                />
              </Box>
            );
          },
        },
        {
          field: 'itemCode',
          headerName: 'Cikkszám',
          type: 'string',
          editable: true,
          sortable: false,
          renderCell: (data) => {
            if (isPinnedRow(data)) return '';
            const valueIndex = getValueIndex(values, data.id);
            const rowItemCode = values.docLine?.[valueIndex]?.itemCode;
            return rowItemCode ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleItemViewOpen(rowItemCode);
                  }}
                  style={{
                    padding: 0,
                    border: 0,
                    background: 'transparent',
                    color: '#0076f8',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    font: 'inherit',
                  }}
                >
                  {rowItemCode}
                </button>
                <IconButton
                  size="small"
                  sx={{
                    color: '#0076f8',
                    backgroundColor: 'transparent',
                    '&:hover': { backgroundColor: 'transparent' },
                  }}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleItemViewOpen(rowItemCode);
                  }}
                >
                  <ArrowOutwardIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              ''
            );
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const rowItemCode = data.value ?? data.row?.itemCode ?? values.docLine?.[valueIndex]?.itemCode ?? null;
            return (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Field
                    name={`docLine.${valueIndex}.itemCode`}
                    type="text"
                    variant="standard"
                    as={AutoCompleteSelectServiceUI}
                    helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.itemCode} />}
                    selectedValue={rowItemCode}
                    selectedLabelValue={rowItemCode ?? ''}
                    service={itemAutocompleteService}
                    serviceRequestBuilder={itemAutocompleteRequestBuilder}
                    searchModeEnabled
                    onChange={async (_e, newVal) => {
                      const val = newVal?.value ?? null;
                      data.api.setEditCellValue({
                        id: data.id,
                        field: data.field,
                        value: val,
                      });

                      await handleItemChange(valueIndex, val);
                    }}
                    onBlur={async () => await validateField(`docLine.${valueIndex}.itemCode`)}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', ml: 0.5 }}>
                  {rowItemCode && (
                    <IconButton
                      size="small"
                      sx={{
                        color: '#0076f8',
                        backgroundColor: 'transparent',
                        '&:hover': { backgroundColor: 'transparent' },
                      }}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleItemViewOpen(rowItemCode);
                      }}
                    >
                      <ArrowOutwardIcon fontSize="small" />
                    </IconButton>
                  )}
                  <AddButtonUi onClick={() => handleItemDialogOpen(data.id)} />
                </Box>
              </Box>
            );
          },
        },
        {
          field: 'itemName',
          headerName: 'Cikknév',
          type: 'string',
          editable: true,
          sortable: false,
          renderCell: (data) => {
            if (isPinnedRow(data)) return '';
            const valueIndex = getValueIndex(values, data.id);
            return values.docLine?.[valueIndex]?.itemName ?? '';
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            return (
              <Field
                name={`docLine.${valueIndex}.itemName`}
                as={InputUI}
                variant="standard"
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.itemName} />}
                onChange={(e) => {
                  setFieldValue(`docLine.${valueIndex}.itemName`, e.target.value ?? null);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: e.target.value,
                  });
                }}
                onBlur={async () => await validateField(`docLine.${valueIndex}.itemName`)}
              />
            );
          },
        },
        {
          field: 'drawingNumber',
          headerName: 'Rajzszám',
          type: 'string',
          editable: true,
          sortable: false,
          renderCell: (data) => {
            if (isPinnedRow(data)) return '';
            const valueIndex = getValueIndex(values, data.id);
            return values.docLine?.[valueIndex]?.drawingNumber ?? '';
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            return (
              <Field
                name={`docLine.${valueIndex}.drawingNumber`}
                as={InputUI}
                type="text"
                variant="standard"
                fullWidth
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.drawingNumber} />}
                onChange={(e) => {
                  const nextValue = e.target.value ?? null;
                  setFieldValue(`docLine.${valueIndex}.drawingNumber`, nextValue);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: nextValue,
                  });
                }}
                onBlur={async () => await validateField(`docLine.${valueIndex}.drawingNumber`)}
              />
            );
          },
        },
        {
          field: 'itemCodeInBp',
          headerName: 'ÜP cikkszám',
          type: 'string',
          editable: true,
          sortable: false,
          renderCell: (data) => {
            if (isPinnedRow(data)) return '';
            const valueIndex = getValueIndex(values, data.id);
            return values.docLine?.[valueIndex]?.itemCodeInBp ?? '';
          },

          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const selectedCatalogRow =
              itemCompanyCatalogRows.find((row) => row.itemCodeInBp === values.docLine?.[valueIndex]?.itemCodeInBp) ??
              null;

            return (
              <>
                <Field
                  name={`docLine.${valueIndex}.itemCodeInBp`}
                  component={AutoCompleteSelectUI}
                  helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.itemCodeInBp} />}
                  selectedValue={selectedCatalogRow?.id ?? null}
                  selectedLabelValue={
                    selectedCatalogRow
                      ? [selectedCatalogRow.itemCodeInBp, selectedCatalogRow.itemNameInBp, selectedCatalogRow.itemCode]
                          .filter(Boolean)
                          .join(' - ')
                      : ''
                  }
                  onChange={async (_e, newVal) => {
                    const selectedRow =
                      itemCompanyCatalogRows.find((row) => row.id === (newVal?.value ?? null)) ?? null;
                    if (!selectedRow) {
                      return;
                    }

                    await handleItemChange(valueIndex, selectedRow.itemCode, false, selectedRow);
                    data.api.setEditCellValue({
                      id: data.id,
                      field: data.field,
                      value: selectedRow.itemCodeInBp ?? null,
                    });
                  }}
                  dataset={itemCompanyCatalogOptions}
                  onBlur={async () => await validateField(`docLine.${valueIndex}.itemCodeInBp`)}
                />
                <Box>
                  <AddButtonUi onClick={() => handleItemCompanyCatalogDialogOpen(data.id)} />
                </Box>
              </>
            );
          },
        },
        {
          field: 'itemNameInBp',
          headerName: 'ÜP cikknév',
          type: 'string',
          editable: false,
          sortable: false,
          renderCell: (data) => {
            if (isPinnedRow(data)) return '';
            const valueIndex = getValueIndex(values, data.id);
            return values.docLine?.[valueIndex]?.itemNameInBp ?? '';
          },
        },
        {
          field: 'qty',
          headerName: 'Mennyiség',
          type: 'number',
          editable: true,
          sortable: false,
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const item = autocompleteData.item.find((i) => i.itemCode === values.docLine?.[valueIndex]?.itemCode);
            const whs = autocompleteData.warehouse.find(
              (w) => w.code === (values.docLine?.[valueIndex]?.whsCode || values.whsCode)
            );

            return (
              <>
                <Field
                  name={`docLine.${valueIndex}.qty`}
                  type="number"
                  variant="standard"
                  as={InputUI}
                  fullWidth
                  helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.qty} />}
                  onChange={(e) => {
                    const rawValue = e.target.value;
                    const normalizedValue = normalizeNumberInput(rawValue);
                    handleQtyChange(valueIndex, rawValue);

                    data.api.setEditCellValue({
                      id: data.id,
                      field: data.field,
                      value: normalizedValue,
                    });
                  }}
                  onBlur={async () => await validateField(`docLine.${valueIndex}.qty`)}
                />
                {((item?.invManagementMethod === InvManagementMethod.BATCH && currentDocType.inventoryAccounting) ||
                  (whs?.binManaged && currentDocType.inventoryAccounting && item?.type !== ItemGroupType.SERVICE)) && (
                  <IconButton
                    aria-label="edit"
                    onClick={() => {
                      setBatchLine({ ...data.row, selectedLineIndex: valueIndex });
                      setBinLine({ ...data.row, selectedLineIndex: valueIndex });
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                )}
              </>
            );
          },
        },
        {
          field: 'quantityUnitCode',
          headerName: 'ME',
          type: 'string',
          editable: false,
          sortable: false,
        },
        {
          field: 'opnQty',
          headerName: 'Nyitott',
          type: 'number',
          editable: false,
          sortable: false,
        },

        {
          field: 'reservedQty',
          headerName: 'Foglalt mennyiség',
          type: 'number',
          editable: true,
          sortable: false,
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);

            return (
              <Field
                name={`docLine.${valueIndex}.reservedQty`}
                type="number"
                variant="standard"
                as={InputUI}
                fullWidth
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.reservedQty} />}
                onChange={(e) => {
                  const rawValue = e.target.value;
                  const normalizedValue = normalizeNumberInput(rawValue);

                  setFieldValue(`docLine.${valueIndex}.reservedQty`, normalizedValue);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: normalizedValue,
                  });
                }}
                onBlur={async () => await validateField(`docLine.${valueIndex}.reservedQty`)}
              />
            );
          },
        },
        {
          field: 'cplQty',
          headerName: 'Lezárt',
          type: 'number',
          editable: false,
          sortable: false,
        },
        {
          field: 'whsCode',
          headerName: 'Raktár',
          type: 'string',
          editable: true,
          sortable: false,
          aggregable: false,
          renderCell: (data) => {
            if (isPinnedRow(data)) return '';
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = autocompleteOptions?.warehouse.find((u) => {
              const docLineCode = values.docLine?.[valueIndex]?.whsCode;
              return u.value === (docLineCode || values.whsCode);
            });

            return findedItem?.label;
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = autocompleteOptions?.warehouse.find((u) => {
              const docLineCode = values.docLine?.[valueIndex]?.whsCode;
              return u.value === (docLineCode || values.whsCode);
            });

            const line = values.docLine?.[valueIndex] ?? data.row;
            const itemCode = line?.itemCode ?? null;
            const isStockBtnDisabled = !itemCode;
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Field
                    name={`docLine.${valueIndex}.whsCode`}
                    as={AutoCompleteSelectUI}
                    helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.whsCode} />}
                    selectedValue={values.docLine?.[valueIndex]?.whsCode}
                    selectedLabelValue={findedItem?.value}
                    //value={findedItem}
                    onChange={(_e, newVal) => {
                      setFieldValue(`docLine.${valueIndex}.whsCode`, newVal?.value ?? null);
                      data.api.setEditCellValue({
                        id: data.id,
                        field: data.field,
                        value: newVal?.value ?? null,
                      });

                      // hozzárendelt sarzsok és rakhelyek ürítése
                      setFieldValue(`docLine.${valueIndex}.docLineBatchSn`, []);
                      setFieldValue(`docLine.${valueIndex}.docLineBin`, []);
                    }}
                    dataset={autocompleteOptions?.warehouse}
                    onBlur={async () => await validateField(`docLine.${valueIndex}.whsCode`)}
                  />
                </Box>

                <Tooltip title={isStockBtnDisabled ? 'Előbb adj meg cikkszámot' : 'Készlet információ'}>
                  <span>
                    <IconButton
                      aria-label="Készlet információ"
                      size="small"
                      disabled={isStockBtnDisabled}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        if (!itemCode) return;

                        const itemFromAutocomplete = autocompleteData.item.find((i) => i.itemCode === itemCode);

                        setItemStockDialogState({
                          open: true,
                          data: itemFromAutocomplete,
                        });
                      }}
                    >
                      <Inventory2OutlinedIcon fontSize="inherit" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            );
          },
        },
        {
          field: 'stockInfo',
          headerName: '',
          type: 'string',
          sortable: false,
          filterable: false,
          editable: false,
          minWidth: 56,
          width: 56,
          align: 'center',
          headerAlign: 'center',
          renderHeader: () => (
            <Tooltip title="Készlet információ">
              <Inventory2OutlinedIcon fontSize="small" />
            </Tooltip>
          ),
          renderCell: (params) => {
            if (isPinnedRow(params)) return '';

            const valueIndex = getValueIndex(values, params.id);
            const line = values.docLine?.[valueIndex] ?? params.row;
            const isDisabled = !line?.itemCode;

            return (
              <Tooltip title={isDisabled ? 'Előbb adj meg cikkszámot' : 'Készlet információ'}>
                <span>
                  <IconButton
                    aria-label="Készlet információ"
                    size="small"
                    disabled={isDisabled}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();

                      if (!line?.itemCode) return;

                      const itemFromAutocomplete = autocompleteData.item.find(
                        (item) => item.itemCode === line.itemCode
                      );

                      setItemStockDialogState({
                        open: true,
                        data: itemFromAutocomplete,
                      });
                    }}
                  >
                    <Inventory2OutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            );
          },
        },
        {
          field: 'toWhsCode',
          headerName: 'Fogadó raktár',
          type: 'string',
          editable: true,
          sortable: false,
          aggregable: false,
          renderCell: (data) => {
            if (isPinnedRow(data)) return '';
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = autocompleteOptions?.warehouse.find((u) => {
              const docLineCode = values.docLine?.[valueIndex]?.toWhsCode;
              return u.value === (docLineCode || values.toWhsCode);
            });

            return findedItem?.label;
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = autocompleteOptions?.warehouse.find((u) => {
              const docLineCode = values.docLine?.[valueIndex]?.toWhsCode;
              return u.value === (docLineCode || values.toWhsCode);
            });
            return (
              <Field
                name={`docLine.${valueIndex}.toWhsCode`}
                as={AutoCompleteSelectUI}
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.toWhsCode} />}
                //selectedValue={values.docLine?.[valueIndex]?.toWhsCode}
                value={findedItem}
                onChange={(_e, newVal) => {
                  setFieldValue(`docLine.${valueIndex}.toWhsCode`, newVal?.value ?? null);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: newVal?.value ?? null,
                  });
                  // default bin setelése raktárból
                  const findedWarehouse = autocompleteData.warehouse.find((whs) => whs.code === newVal?.value);
                  if (findedWarehouse?.binManaged) {
                    setFieldValue(`docLine.${valueIndex}.toBinId`, findedWarehouse.dispatchBin ?? null);
                    data.api.setEditCellValue({
                      id: data.id,
                      field: 'toBinId',
                      value: findedWarehouse.dispatchBin ?? null,
                    });

                    return;
                  }

                  setFieldValue(`docLine.${valueIndex}.toBinId`, null);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: 'toBinId',
                    value: null,
                  });
                }}
                dataset={autocompleteOptions?.warehouse}
                onBlur={async () => await validateField(`docLine.${valueIndex}.toWhsCode`)}
              />
            );
          },
        },
        {
          field: 'toBinId',
          headerName: 'Fogadó rakhely',
          type: 'string',
          editable: true,
          sortable: false,
          aggregable: false,
          renderCell: (data) => {
            if (isPinnedRow(data)) return '';
            const valueIndex = getValueIndex(values, data.id);
            const findedWarehouse = autocompleteData?.warehouse?.find(
              (whs) => whs.code === values.docLine?.[valueIndex]?.toWhsCode
            );

            const findedBin = findedWarehouse?.bin.find((bin) => bin.binId === values.docLine?.[valueIndex]?.toBinId);

            return findedBin?.binCode ?? '';
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const findedWarehouse = autocompleteData.warehouse.find(
              (whs) => whs.code === values.docLine?.[valueIndex]?.toWhsCode
            );
            const binOptions = generateAutocompleteItems(findedWarehouse?.bin, 'binCode', 'binId');

            const findedItem = binOptions.find((b) => b.value === values.docLine?.[valueIndex]?.toBinId);
            const toWhsKey = values.docLine?.[valueIndex]?.toWhsCode ?? 'no-whs';

            return (
              <Field
                key={`to-bin-${valueIndex}-${toWhsKey}`}
                name={`docLine.${valueIndex}.toBinId`}
                as={AutoCompleteSelectUI}
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.toBinId} />}
                //selectedValue={values.docLine?.[valueIndex]?.toBinId}
                value={findedItem}
                onChange={(_e, newVal) => {
                  setFieldValue(`docLine.${valueIndex}.toBinId`, newVal?.value ?? null);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: newVal?.value ?? null,
                  });
                }}
                dataset={binOptions}
                onBlur={async () => await validateField(`docLine.${valueIndex}.toBinId`)}
              />
            );
          },
        },
        {
          field: 'unitPrice',
          headerName: 'Egységár',
          type: 'number',
          editable: true,
          sortable: false,
          renderCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            return values.docLine?.[valueIndex]?.unitPrice
              ? formatCurrency(values.docLine?.[valueIndex]?.unitPrice, docRounding)
              : ' ';
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);

            return (
              <Field
                name={`docLine.${valueIndex}.unitPrice`}
                type="text"
                variant="standard"
                as={InputUI}
                fullWidth
                inputProps={{ inputMode: 'decimal' }}
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.unitPrice} />}
                onChange={(e) => {
                  const value = normalizeNumberInput(e.target.value);
                  if (!PRICE_INPUT_PATTERN.test(value)) return;

                  setFieldValue(`docLine.${valueIndex}.unitPrice`, value);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value,
                  });

                  if (isIncompletePriceInput(value)) return;

                  handlePriceChange(valueIndex, value, shouldPreservePriceInput(value, docRounding));
                }}
                onBlur={async () => await validateField(`docLine.${valueIndex}.unitPrice`)}
              />
            );
          },
        },

        {
          field: 'grossUnitPrice',
          headerName: 'Bruttó egységár',
          type: 'number',
          editable: false,
          sortable: false,
          renderCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            return values.docLine?.[valueIndex]?.grossUnitPrice
              ? formatCurrency(values.docLine?.[valueIndex]?.grossUnitPrice ?? '', docRounding)
              : '';
          },
        },

        {
          field: 'discountPercent',
          headerName: 'Kedvezmény %',
          type: 'number',
          editable: true,
          sortable: false,
          renderCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            return values.docLine?.[valueIndex]?.discountPercent
              ? `${formatCurrency(values.docLine?.[valueIndex]?.discountPercent ?? '', docRounding)} %`
              : '';
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);

            return (
              <Field
                name={`docLine.${valueIndex}.discountPercent`}
                type="number"
                variant="standard"
                as={InputUI}
                value={values.docLine?.[valueIndex]?.discountPercent ?? ''}
                fullWidth
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.discountPercent} />}
                onChange={(e) => {
                  const value = e.target.value;
                  handleDiscountPercentChange(valueIndex, e.target.value ?? '');

                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: value,
                  });
                }}
                onBlur={async () => await validateField(`docLine.${valueIndex}.discountPercent`)}
              />
            );
          },
        },

        {
          field: 'baseUnitPrice',
          headerName: 'Alap egységár',
          type: 'number',
          editable: false,
          sortable: false,
          valueGetter: (value) => parseFloat(value),
          valueFormatter: (params) => {
            return params ? formatCurrency(params, docRounding) : '';
          },
        },

        {
          field: 'baseGrossUnitPrice',
          headerName: 'Alap bruttó egységár',
          type: 'number',
          editable: true,
          sortable: false,
          valueGetter: (value) => {
            return parseFloat(value);
          },
          valueFormatter: (params) => {
            return params ? formatCurrency(params, docRounding) : '';
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);

            return (
              <Field
                name={`docLine.${valueIndex}.baseGrossUnitPrice`}
                type="text"
                variant="standard"
                as={InputUI}
                fullWidth
                inputProps={{ inputMode: 'decimal' }}
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.baseGrossUnitPrice} />}
                onChange={(e) => {
                  const value = normalizeNumberInput(e.target.value);
                  if (!PRICE_INPUT_PATTERN.test(value)) return;

                  setFieldValue(`docLine.${valueIndex}.baseGrossUnitPrice`, value);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value,
                  });

                  if (isIncompletePriceInput(value)) return;

                  handleGrossPriceChange(valueIndex, value, values.docLine?.[valueIndex]?.vatPercent);
                }}
                onBlur={async () => await validateField(`docLine.${valueIndex}.baseGrossUnitPrice`)}
              />
            );
          },
        },

        {
          field: 'currencyCode',
          headerName: 'Ár pénznem',
          type: 'string',
          editable: false,
          sortable: false,
          renderCell: (data) => {
            if (isPinnedRow(data)) return '';
            const currFromPl = autocompleteOptions?.priceList?.find((u) => u.id === values.priceListId);
            return currFromPl?.currencyCode ?? values.currencyCode;
          },
        },

        {
          field: 'vatCode',
          headerName: 'Áfakód',
          type: 'string',
          editable: true,
          sortable: false,
          renderCell: (data) => {
            if (isPinnedRow(data)) return '';
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = autocompleteOptions?.vatCode?.find((u) => {
              return u.value === values.docLine?.[valueIndex]?.vatCode;
            });

            return findedItem?.label;
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const currentVatCode = data.value ?? data.row?.vatCode ?? values.docLine?.[valueIndex]?.vatCode ?? null;
            const findedItem = autocompleteOptions?.vatCode?.find((u) => {
              return u.value === currentVatCode;
            });
            return (
              <Field
                name={`docLine.${valueIndex}.vatCode`}
                as={AutoCompleteSelectUI}
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.vatCode} />}
                selectedValue={currentVatCode}
                selectedLabelValue={findedItem?.label}
                value={findedItem}
                onChange={(_e, newVal) => {
                  setFieldValue(`docLine.${valueIndex}.vatCode`, newVal?.value ?? null);
                  const findedVat = autocompleteData.vatCode.find((u) => u.code === newVal?.value);
                  setFieldValue(`docLine.${valueIndex}.vatPercent`, findedVat?.percentage ?? null);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: newVal?.value ?? null,
                  });
                  if (isGrossDrivenCalculation(valueIndex)) {
                    calculateGrossDrivenPrice(
                      valueIndex,
                      values.docLine[valueIndex].qty,
                      values.docLine[valueIndex].grossUnitPrice,
                      findedVat?.percentage ?? '0'
                    );
                  } else {
                    calculatePrice(
                      valueIndex,
                      values.docLine[valueIndex].qty,
                      values.docLine[valueIndex].unitPrice,
                      findedVat?.percentage ?? '0'
                    );

                    const grossPrice = round(
                      calculateGrossPrice(values.docLine[valueIndex].unitPrice, findedVat?.percentage ?? '0'),
                      docRounding
                    );
                    setFieldValue(`docLine.${valueIndex}.grossUnitPrice`, grossPrice);
                    setFieldValue(`docLine.${valueIndex}.baseGrossUnitPrice`, grossPrice);

                    apiRef.current.setEditCellValue({
                      id: data.id,
                      field: 'baseGrossUnitPrice',
                      value: grossPrice,
                    });
                  }
                }}
                dataset={autocompleteOptions?.vatCode}
                onBlur={async () => await validateField(`docLine.${valueIndex}.vatCode`)}
              />
            );
          },
        },

        {
          field: 'vatPercent',
          headerName: 'Áfa %',
          type: 'number',
          editable: false,
          sortable: false,
        },

        {
          field: 'netPrice',
          headerName: 'Nettó érték',
          type: 'number',
          editable: true,
          sortable: false,
          valueGetter: (value) => parseFloat(value),
          valueFormatter: (params) => {
            return params ? formatCurrency(params, selectedDocCur?.amountRounding ?? 0) : '';
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);

            return (
              <Field
                name={`docLine.${valueIndex}.netPrice`}
                type="text"
                variant="standard"
                as={InputUI}
                fullWidth
                inputProps={{ inputMode: 'decimal' }}
                //disabled={!editable}
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.netPrice} />}
                onChange={(e) => {
                  const value = normalizeNumberInput(e.target.value);
                  if (!PRICE_INPUT_PATTERN.test(value)) return;

                  if (isIncompletePriceInput(value)) {
                    setFieldValue(`docLine.${valueIndex}.netPrice`, value);
                    data.api.setEditCellValue({
                      id: data.id,
                      field: data.field,
                      value,
                    });
                    return;
                  }

                  // ha nincs áfakód noti
                  // ha nincs mennyiség noti

                  if (values.docLine?.[valueIndex]?.vatCode == null) {
                    addNotification({
                      content: 'Kérem válasszon áfakódot a sorhoz, mielőtt megadja a nettó értéket!',
                      type: NotificationType.WARNING,
                    });
                    return;
                  }

                  if (!values.docLine?.[valueIndex]?.qty || Number(values.docLine?.[valueIndex]?.qty) === 0) {
                    addNotification({
                      content: 'Kérem adjon meg mennyiséget a sorhoz, mielőtt megadja a nettó értéket!',
                      type: NotificationType.WARNING,
                    });
                    return;
                  }

                  handleNetPriceChange(valueIndex, value ?? '');
                }}
                onBlur={async () => await validateField(`docLine.${valueIndex}.netPrice`)}
              />
            );
          },
        },

        {
          field: 'vatValue',
          headerName: 'Áfa érték',
          type: 'number',
          editable: false,
          sortable: false,
          valueGetter: (value) => parseFloat(value),
          valueFormatter: (params) => {
            return params ? formatCurrency(params, selectedDocCur?.amountRounding ?? 0) : '';
          },
        },

        {
          field: 'grossPrice',
          headerName: 'Bruttó érték',
          type: 'number',
          editable: false,
          sortable: false,
          valueGetter: (value) => parseFloat(value),
          valueFormatter: (params) => {
            return params ? formatCurrency(params, selectedDocCur?.amountRounding ?? 0) : '';
          },
        },

        ...(isDownPaymentDocument
          ? [
              {
                field: 'appliedGrossAmount',
                headerName: 'Felhasznált előleg (bruttó)',
                type: 'number',
                editable: false,
                sortable: false,
                valueGetter: (value) => Number(value ?? 0),
                valueFormatter: (value) => formatCurrency(value ?? 0, selectedDocCur?.amountRounding ?? 0),
              },
              {
                field: 'openDownPaymentAmount',
                headerName: 'Nyitott előlegegyenleg (bruttó)',
                type: 'number',
                editable: false,
                sortable: false,
                filterable: false,
                valueGetter: (_value, row) =>
                  row?.id === 'pinned'
                    ? row.openDownPaymentAmount
                    : getOpenDownPaymentAmount(row?.grossPrice, row?.appliedGrossAmount),
                valueFormatter: (value) => formatCurrency(value ?? 0, selectedDocCur?.amountRounding ?? 0),
              },
            ]
          : []),

        {
          field: 'netPriceInOwnCur',
          headerName: 'Nettó saját pénznemben',
          type: 'number',
          editable: false,
          sortable: false,
          valueGetter: (value) => parseFloat(value),
          valueFormatter: (params) => {
            return params ? formatCurrency(params, selectedOwnCur?.amountRounding ?? 0) : '';
          },
        },

        {
          field: 'vatValueInOwnCur',
          headerName: 'Áfa saját pénznemben',
          type: 'number',
          editable: false,
          sortable: false,
          valueGetter: (value) => parseFloat(value),
          valueFormatter: (params) => {
            return params ? formatCurrency(params, selectedOwnCur?.amountRounding ?? 0) : '';
          },
        },

        {
          field: 'grossPriceInOwnCur',
          headerName: 'Bruttó saját pénznemben',
          type: 'number',
          editable: false,
          sortable: false,
          valueGetter: (value) => parseFloat(value),
          valueFormatter: (params) => {
            return params ? formatCurrency(params, selectedOwnCur?.amountRounding ?? 0) : '';
          },
        },
        ...(documentData.additionalCost
          ? [
              {
                field: 'finalUnitCost',
                headerName: 'Költséggel növelt egységár saját pénznemben',
                type: 'number',
                editable: false,
                sortable: false,
                valueGetter: (value) => (value ? parseFloat(value) : ''),
                valueFormatter: (params) => {
                  return params ? formatCurrency(params, docRounding) : '';
                },
              },
              {
                field: 'finalLineValue',
                headerName: 'Költséggel növelt nettó saját pénznemben',
                type: 'number',
                editable: false,
                sortable: false,
                valueGetter: (value) => (value ? parseFloat(value) : ''),
                valueFormatter: (params) => {
                  return params ? formatCurrency(params, docRounding) : '';
                },
              },
              {
                field: 'landedCost',
                headerName: 'Mellékköltség',
                type: 'boolean',
                editable: true,
                sortable: false,
                valueGetter: (value, row) => {
                  const valueIndex = getValueIndex(values, row.id);
                  return values.docLine?.[valueIndex]?.landedCost;
                },
                renderEditCell: (data) => {
                  const valueIndex = getValueIndex(values, data.id);
                  const findedItem = autocompleteData.item.find(
                    (i) => i.itemCode === values.docLine?.[valueIndex]?.itemCode
                  );
                  return (
                    <Field
                      name={`docLine.${valueIndex}.landedCost`}
                      type="checkbox"
                      component={CheckboxUI}
                      helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.landedCost} />}
                      checked={values.docLine?.[valueIndex]?.landedCost ?? false}
                      variant="standard"
                      disabled={findedItem?.type !== 'SERVICE'}
                      onChange={(e) => {
                        setFieldValue(`docLine.${valueIndex}.landedCost`, e.target.checked);
                        data.api.setEditCellValue({
                          id: data.id,
                          field: data.field,
                          value: e.target.checked,
                        });
                      }}
                    />
                  );
                },
              },
            ]
          : []),

        {
          field: 'weightChangeNumber',
          headerName: 'Súly váltó',
          type: 'number',
          editable: false,
          sortable: false,
        },

        {
          field: 'weight',
          headerName: 'Súly',
          type: 'number',
          editable: true,
          sortable: false,
          valueGetter: (value) => parseFloat(value),
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);

            return (
              <Field
                name={`docLine.${valueIndex}.weight`}
                type="number"
                variant="standard"
                as={InputUI}
                fullWidth
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.weight} />}
                onChange={(e) => {
                  setFieldValue(`docLine.${valueIndex}.weight`, e.target.value ?? '');
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: e.target.value,
                  });
                }}
                onBlur={async () => await validateField(`docLine.${valueIndex}.weight`)}
              />
            );
          },
        },

        {
          field: 'projectId',
          headerName: 'Projekt',
          type: 'string',
          editable: true,
          sortable: false,
          renderCell: (data) => {
            if (isPinnedRow(data)) return '';
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = autocompleteOptions?.project.find(
              (u) => u.value === values.docLine?.[valueIndex]?.projectId
            );
            return findedItem?.label;
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = autocompleteOptions?.project.find(
              (u) => u.value === values.docLine?.[valueIndex]?.projectId
            );
            return (
              <Field
                name={`docLine.${valueIndex}.projectId`}
                as={AutoCompleteSelectUI}
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.projectId} />}
                selectedValue={values.docLine?.[valueIndex]?.projectId}
                value={findedItem}
                onChange={(_e, newVal) => {
                  setFieldValue(`docLine.${valueIndex}.projectId`, newVal?.value ?? null);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: newVal?.value ?? null,
                  });

                  const project = autocompleteData.project.find((p) => p.projectId === newVal?.value);
                  setFieldValue(`docLine.${valueIndex}.projectOrderNumber`, project?.orderNumber ?? '');
                }}
                dataset={autocompleteOptions?.project}
                onBlur={async () => await validateField(`docLine.${valueIndex}.projectId`)}
              />
            );
          },
        },

        {
          field: 'projectOrderNumber',
          headerName: getProjectOrderNumberName(params),
          type: 'string',
          editable: false,
          sortable: false,
        },

        {
          field: 'costCenterCode',
          headerName: 'Költséghely',
          type: 'string',
          editable: true,
          sortable: false,
          renderCell: (data) => {
            if (isPinnedRow(data)) return '';
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = autocompleteOptions?.costCenter.find(
              (u) => u.value === values.docLine?.[valueIndex]?.costCenterCode
            );
            return findedItem?.label;
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = autocompleteOptions?.costCenter.find(
              (u) => u.value === values.docLine?.[valueIndex]?.costCenterCode
            );
            return (
              <Field
                name={`docLine.${valueIndex}.costCenterCode`}
                as={AutoCompleteSelectUI}
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.costCenterCode} />}
                selectedValue={values.docLine?.[valueIndex]?.costCenterCode}
                value={findedItem}
                onChange={(_e, newVal) => {
                  setFieldValue(`docLine.${valueIndex}.costCenterCode`, newVal?.value ?? null);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: newVal?.value ?? null,
                  });
                }}
                dataset={autocompleteOptions?.costCenter}
                onBlur={async () => await validateField(`docLine.${valueIndex}.costCenterCode`)}
              />
            );
          },
        },

        {
          field: 'itemReference',
          headerName: 'Tétel hivatkozás',
          type: 'string',
          editable: true,
          sortable: false,
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            return (
              <Field
                name={`docLine.${valueIndex}.itemReference`}
                as={InputUI}
                type="text"
                variant="standard"
                fullWidth
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.itemReference} />}
                onChange={(e) => {
                  setFieldValue(`docLine.${valueIndex}.itemReference`, e.target.value ?? '');
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: e.target.value,
                  });
                }}
                onBlur={async () => await validateField(`docLine.${valueIndex}.itemReference`)}
              />
            );
          },
        },

        {
          field: 'internalNote',
          headerName: 'Belső megjegyzés',
          type: 'string',
          editable: true,
          sortable: false,
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            return (
              <Field
                name={`docLine.${valueIndex}.internalNote`}
                as={InputUI}
                type="text"
                variant="standard"
                fullWidth
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.internalNote} />}
                onChange={(e) => {
                  setFieldValue(`docLine.${valueIndex}.internalNote`, e.target.value ?? '');
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: e.target.value,
                  });
                }}
                onBlur={async () => await validateField(`docLine.${valueIndex}.internalNote`)}
              />
            );
          },
        },
        {
          field: 'bpNote',
          headerName: 'Üp megjegyzés',
          type: 'string',
          editable: true,
          sortable: false,
          renderCell: (params) => {
            if (isPinnedRow(params)) return '';
            const value = params?.row?.bpNote ?? '';
            return (
              <Tooltip title={<Box sx={{ whiteSpace: 'pre-wrap' }}>{value}</Box>}>
                <Box
                  onContextMenu={(event) => {
                    if (viewField || params.row.lineStatus !== DocStatus.OPENED) return;
                    event.preventDefault();
                    event.stopPropagation();
                    setTemplateContextMenu({
                      mouseX: event.clientX + 2,
                      mouseY: event.clientY - 6,
                      rowId: params.row.id,
                    });
                  }}
                  sx={{
                    width: '100%',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {value.replace(/(?:\r\n|\r|\n)[\s\S]*$/, '...')}
                </Box>
              </Tooltip>
            );
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            return (
              <Field
                name={`docLine.${valueIndex}.bpNote`}
                as={InputUI}
                type="text"
                variant="standard"
                fullWidth
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.bpNote} />}
                onChange={(e) => {
                  setFieldValue(`docLine.${valueIndex}.bpNote`, e.target.value ?? '');
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: e.target.value,
                  });
                }}
                onBlur={async () => await validateField(`docLine.${valueIndex}.bpNote`)}
                onContextMenu={(event) => {
                  if (viewField || data.row.lineStatus !== DocStatus.OPENED) return;
                  event.preventDefault();
                  event.stopPropagation();
                  setTemplateContextMenu({
                    mouseX: event.clientX + 2,
                    mouseY: event.clientY - 6,
                    rowId: data.row.id,
                  });
                }}
              />
            );
          },
        },
        {
          field: 'lineStatus',
          headerName: 'Státusz',
          type: 'string',
          editable: false,
          sortable: false,
          renderCell: (data) => {
            if (isPinnedRow(data)) return '';
            const valueIndex = getValueIndex(values, data.id);
            return DocStatusList.find((i) => i.value === values.docLine?.[valueIndex]?.lineStatus)?.label;
          },
        },
        {
          field: 'baseDocType',
          headerName: 'Alapbizonylat neve',
          type: 'string',
          editable: false,
          sortable: false,
          renderCell: (data) => {
            if (isPinnedRow(data)) return '';
            const valueIndex = getValueIndex(values, data.id);
            return docTypes.find((i) => i.documentTypeId === values.docLine?.[valueIndex]?.baseDocType)?.name;
          },
        },
        {
          field: 'baseDocnum',
          headerName: 'Alapbizonylat száma',
          type: 'string',
          editable: false,
          sortable: false,
        },
        {
          field: 'baseLine',
          headerName: 'Alapbizonylat sora',
          type: 'string',
          editable: false,
          sortable: false,
        },
        {
          field: 'dueDate',
          headerName: 'Határidő',
          type: 'date',
          sortable: false,
          filterable: false,
          editable: true,
          valueGetter: (_data, row) => {
            return new Date(row.dueDate);
          },
          renderCell: (data) => {
            if (isPinnedRow(data)) return '';
            return dayjs(data.row.dueDate).format('YYYY.MM.DD');
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            return (
              <Field
                name={`docLine.${valueIndex}.dueDate`}
                component={DatePickerUI}
                type="date"
                variant="standard"
                fullWidth
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.dueDate} />}
                value={values.docLine?.[valueIndex]?.dueDate}
                onChange={(date) => {
                  setFieldValue(`docLine.${valueIndex}.dueDate`, date);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: new Date(date),
                  });
                }}
                onBlur={async () => await validateField(`docLine.${valueIndex}.dueDate`)}
              />
            );
          },
        },
        {
          field: 'isBillable',
          headerName: 'Számlázandó',
          type: 'boolean',
          editable: true,
          sortable: false,
          renderCell: (data) => {
            if (isPinnedRow(data)) return '';
            const Icon = data.row?.isBillable ? GridCheckIcon : GridCloseIcon;
            return <Icon fontSize="small" />;
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            return (
              <Field
                name={`docLine.${valueIndex}.isBillable`}
                type="checkbox"
                component={CheckboxUI}
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.isBillable} />}
                checked={values.docLine?.[valueIndex]?.isBillable ?? false}
                variant="standard"
                onChange={(e) => {
                  setFieldValue(`docLine.${valueIndex}.isBillable`, e.target.checked);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: e.target.checked,
                  });
                }}
              />
            );
          },
        },
        ...(params?.USE_AME === 'Y' ? [{
          field: 'ameCode',
          headerName: 'ÁME-kód',
          type: 'string',
          editable: true,
          renderCell: (data) => {
            if (isPinnedRow(data)) return '';
            const description = ameCodes.find((row) => row.code === data.row.ameCode)?.description ?? '';
            return <Tooltip title={<span style={{ whiteSpace: 'pre-wrap' }}>{description}</span>}>
              <span>{data.row.ameCode ?? ''}</span>
            </Tooltip>;
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            return <AMESelect codes={ameCodes} value={data.value ?? values.docLine?.[valueIndex]?.ameCode}
              onChange={(_event, option) => {
                const code = option?.value ?? null;
                setFieldValue(`docLine.${valueIndex}.ameCode`, code);
                data.api.setEditCellValue({ id: data.id, field: data.field, value: code });
              }} />;
          },
        }] : []),
        {
          field: 'warranty',
          headerName: 'Garanciális',
          type: 'boolean',
          editable: true,
          sortable: false,
          renderCell: (data) => {
            if (isPinnedRow(data)) return '';
            const Icon = data.row?.warranty ? GridCheckIcon : GridCloseIcon;
            return <Icon fontSize="small" />;
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            return (
              <Field
                name={`docLine.${valueIndex}.warranty`}
                type="checkbox"
                component={CheckboxUI}
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.warranty} />}
                checked={values.docLine?.[valueIndex]?.warranty ?? false}
                variant="standard"
                onChange={(e) => {
                  setFieldValue(`docLine.${valueIndex}.warranty`, e.target.checked);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: e.target.checked,
                  });
                }}
              />
            );
          },
        },
        ...(parseInt(docTypeId) === DocumentType.CustomerOrder
          ? [
              {
                field: 'productionPrepared',
                headerName: 'Gyártásra előkészített',
                type: 'boolean',
                editable: true,
                sortable: false,
                renderCell: (data) => {
                  if (isPinnedRow(data)) return '';
                  const Icon = data.row?.productionPrepared ? GridCheckIcon : GridCloseIcon;
                  return <Icon fontSize="small" />;
                },
                renderEditCell: (data) => {
                  const valueIndex = getValueIndex(values, data.id);
                  return (
                    <Field
                      name={`docLine.${valueIndex}.productionPrepared`}
                      type="checkbox"
                      component={CheckboxUI}
                      helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.productionPrepared} />}
                      checked={values.docLine?.[valueIndex]?.productionPrepared ?? false}
                      variant="standard"
                      onChange={(e) => {
                        setFieldValue(`docLine.${valueIndex}.productionPrepared`, e.target.checked);
                        data.api.setEditCellValue({
                          id: data.id,
                          field: data.field,
                          value: e.target.checked,
                        });
                      }}
                    />
                  );
                },
              },
            ]
          : []),
        ...(supportingDocumentAssignmentEnabled
          ? [
              {
                field: 'supportingDocumentAssignment',
                headerName: 'Kísérő dokumentumok',
                sortable: false,
                filterable: false,
                editable: false,
                minWidth: 56,
                width: 56,
                align: 'center',
                headerAlign: 'center',
                renderHeader: () => (
                  <Tooltip title="Kísérő dokumentumok">
                    <FilePresentOutlinedIcon fontSize="small" />
                  </Tooltip>
                ),
                renderCell: (data) => {
                  if (isPinnedRow(data)) return '';

                  const valueIndex = getValueIndex(values, data.id);
                  const line = values.docLine?.[valueIndex] ?? data.row;
                  const isDisabled = !line?.itemCode;
                  const selectedCount = (values.supportingDocumentAssignment ?? []).filter(
                    (assignment) => assignment.itemCode === line?.itemCode
                  ).length;

                  return (
                    <Tooltip title={isDisabled ? 'Előbb adj meg cikkszámot' : 'Kísérő dokumentumok'}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={isDisabled}
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();

                            if (!line?.itemCode) return;

                            setSupportingDocumentAssignmentDialogState({
                              open: true,
                              itemCode: line.itemCode,
                            });
                          }}
                        >
                          <FilePresentOutlinedIcon fontSize="small" />
                          {selectedCount > 0 && <span className="ml-1 text-xs">{selectedCount}</span>}
                        </IconButton>
                      </span>
                    </Tooltip>
                  );
                },
              },
            ]
          : []),
        ...(!currentDocType?.inventoryAccounting
          ? [
              {
                field: 'prevPrices',
                headerName: '',
                type: 'string',
                sortable: false,
                filterable: false,
                editable: false,
                minWidth: 56,
                width: 56,
                align: 'center',
                headerAlign: 'center',
                renderHeader: () => (
                  <Tooltip title="Korábbi árak">
                    <AttachMoneyIcon fontSize="small" />
                  </Tooltip>
                ),
                renderCell: (params) => {
                  if (isPinnedRow(params)) return '';

                  const valueIndex = getValueIndex(values, params.id);
                  const line = values.docLine?.[valueIndex] ?? params.row;
                  const isDisabled = !line?.itemCode;

                  return (
                    <Tooltip title={isDisabled ? 'Előbb adj meg cikkszámot' : 'Korábbi árak'}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={isDisabled}
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();

                            if (!line?.itemCode) return;

                            setPrevPricesDialogState({
                              open: true,
                              line: {
                                itemCode: line.itemCode,
                                itemName: line.itemName,
                              },
                            });
                          }}
                        >
                          <AttachMoneyIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  );
                },
              },
            ]
          : []),
        {
          field: 'actions',
          type: 'actions',
          headerName: '',
          cellClassName: 'actions',
          sortable: false,
          getActions: (params) => {
            if (isPinnedRow(params)) return [];
            const { id, row } = params;
            const foundedItemType = autocompleteData?.item?.find((item) => item.itemCode === row.itemCode)?.type;
            return [
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label="Delete"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  event.defaultMuiPrevented = true;
                  const valueIndex = getValueIndex(values, id);
                  setTimeout(() => handleRemoveLine(remove, valueIndex), 0);
                }}
                color="inherit"
                disabled={
                  disabled ||
                  row.deletable === false ||
                  (currentDocType.inventoryAccounting &&
                    foundedItemType !== ItemGroupType.SERVICE &&
                    !row.isNew &&
                    selectedValues.docStatus !== DocStatus.PLANNED)
                }
              />,
            ];
          },
        },
        ...jokerColumns,
      ]),
    [
      values,
      errors,
      autocompleteData,
      autocompleteOptions,
      ameCodes,
      currentDocType,
      selectedValues,
      docTypes,
      config,
      documentData,
      params,
      handleOrderChange,
      isDownPaymentDocument,
    ]
  );

  const calculateGrossDrivenPrice = (valueIndex, _qty, _grossUnitPrice, _vatPercent) => {
    const qty = parseLocaleNumber(_qty ?? '0');
    const grossUnitPrice = parseLocaleNumber(_grossUnitPrice ?? '0');
    const vatPercent = parseLocaleNumber(_vatPercent ?? '0');

    if (!Number.isFinite(qty) || qty === 0) {
      setFieldValue(`docLine.${valueIndex}.netPrice`, 0);
      setFieldValue(`docLine.${valueIndex}.vatValue`, 0);
      setFieldValue(`docLine.${valueIndex}.grossPrice`, 0);
      setFieldValue(`docLine.${valueIndex}.netPriceInOwnCur`, 0);
      setFieldValue(`docLine.${valueIndex}.vatValueInOwnCur`, 0);
      setFieldValue(`docLine.${valueIndex}.grossPriceInOwnCur`, 0);
      return;
    }

    const amountRounding = selectedDocCur?.amountRounding ?? 0;
    const ownAmountRounding = selectedOwnCur?.amountRounding ?? 0;
    const grossPrice = round(multiply(qty, grossUnitPrice), amountRounding);
    const netPrice = round(calculateNetPrice(grossPrice, vatPercent), amountRounding);
    const vatValue = subtract(grossPrice, netPrice);
    const netUnitPrice = round(calculateNetPrice(grossUnitPrice, vatPercent), docRounding);

    setFieldValue(`docLine.${valueIndex}.unitPrice`, netUnitPrice);
    setFieldValue(`docLine.${valueIndex}.grossUnitPrice`, grossUnitPrice);
    setFieldValue(`docLine.${valueIndex}.netPrice`, netPrice);
    setFieldValue(`docLine.${valueIndex}.vatValue`, vatValue);
    setFieldValue(`docLine.${valueIndex}.grossPrice`, grossPrice);

    const exchangeRate = Number(values.exchangeRate);
    const grossPriceInOwnCur = round(
      multiply(grossPrice, exchangeRate > 0 ? exchangeRate : 1),
      ownAmountRounding
    );
    const netPriceInOwnCur = round(calculateNetPrice(grossPriceInOwnCur, vatPercent), ownAmountRounding);
    const vatValueInOwnCur = subtract(grossPriceInOwnCur, netPriceInOwnCur);

    setFieldValue(`docLine.${valueIndex}.netPriceInOwnCur`, netPriceInOwnCur);
    setFieldValue(`docLine.${valueIndex}.vatValueInOwnCur`, vatValueInOwnCur);
    setFieldValue(`docLine.${valueIndex}.grossPriceInOwnCur`, grossPriceInOwnCur);

    apiRef.current?.setEditCellValue({
      id: values.docLine[valueIndex].id,
      field: 'netPrice',
      value: netPrice,
    });
    apiRef.current?.setEditCellValue({
      id: values.docLine[valueIndex].id,
      field: 'vatValue',
      value: vatValue,
    });
    apiRef.current?.setEditCellValue({
      id: values.docLine[valueIndex].id,
      field: 'grossPrice',
      value: grossPrice,
    });
  };

  const calculatePrice = (valueIndex, _qty, _unitPrice, _vatPercent) => {
    const qty = parseLocaleNumber(_qty ?? '0');
    const unitPrice = parseLocaleNumber(_unitPrice ?? '0');
    const vatPercent = parseLocaleNumber(_vatPercent ?? '0');

    if (!Number.isFinite(qty) || qty === 0) {
      setFieldValue(`docLine.${valueIndex}.netPrice`, 0);
      setFieldValue(`docLine.${valueIndex}.vatValue`, 0);
      setFieldValue(`docLine.${valueIndex}.grossPrice`, 0);
      setFieldValue(`docLine.${valueIndex}.grossUnitPrice`, 0);

      apiRef.current?.setEditCellValue({
        id: values.docLine[valueIndex].id,
        field: 'netPrice',
        value: 0,
      });
      apiRef.current?.setEditCellValue({
        id: values.docLine[valueIndex].id,
        field: 'grossPrice',
        value: 0,
      });
      apiRef.current?.setEditCellValue({
        id: values.docLine[valueIndex].id,
        field: 'grossUnitPrice',
        value: 0,
      });
      apiRef.current?.setEditCellValue({
        id: values.docLine[valueIndex].id,
        field: 'vatValue',
        value: 0,
      });

      return;
    }

    const netPrice = round(multiply(unitPrice, qty), selectedDocCur?.amountRounding ?? 0);
    const vatValue =
      vatPercent > 0 ? round(multiply(netPrice, divide(vatPercent, 100)), selectedDocCur?.amountRounding ?? 0) : 0;

    const grossPrice = round(add(netPrice, vatValue), selectedDocCur?.amountRounding ?? 0);

    const grossUnitPrice = round(divide(grossPrice, qty), docRounding);

    setFieldValue(`docLine.${valueIndex}.netPrice`, netPrice);

    apiRef.current?.setEditCellValue({
      id: values.docLine[valueIndex].id,
      field: 'netPrice',
      value: netPrice,
    });

    setFieldValue(`docLine.${valueIndex}.vatValue`, vatValue);
    setFieldValue(`docLine.${valueIndex}.grossPrice`, grossPrice);
    setFieldValue(`docLine.${valueIndex}.grossUnitPrice`, grossUnitPrice);

    const exchangeRate = Number(values.exchangeRate);
    const netPriceInOwnCur = round(
      multiply(netPrice, exchangeRate > 0 ? exchangeRate : 1),
      selectedOwnCur?.amountRounding ?? 0
    );
    const vatValueInOwnCur = round(
      multiply(vatValue, exchangeRate > 0 ? exchangeRate : 1),
      selectedOwnCur?.amountRounding ?? 0
    );
    const grossPriceInOwnCur = round(
      multiply(grossPrice, exchangeRate > 0 ? exchangeRate : 1),
      selectedOwnCur?.amountRounding ?? 0
    );
    setFieldValue(`docLine.${valueIndex}.netPriceInOwnCur`, netPriceInOwnCur);
    setFieldValue(`docLine.${valueIndex}.vatValueInOwnCur`, vatValueInOwnCur);
    setFieldValue(`docLine.${valueIndex}.grossPriceInOwnCur`, grossPriceInOwnCur);
  };

  const calculateWeight = (valueIndex, _qty, _weightChangeNumber) => {
    const qty = parseLocaleNumber(_qty ?? '0');
    const weightChangeNumber = parseLocaleNumber(_weightChangeNumber ?? '0');

    if (!Number.isFinite(qty) || !Number.isFinite(weightChangeNumber)) {
      setFieldValue(`docLine.${valueIndex}.weight`, 0);

      apiRef.current.setEditCellValue({
        id: values.docLine[valueIndex].id,
        field: 'weight',
        value: 0,
      });
      return;
    }

    const weight = round(multiply(qty, weightChangeNumber), 2);
    setFieldValue(`docLine.${valueIndex}.weight`, weight);

    apiRef.current.setEditCellValue({
      id: values.docLine[valueIndex].id,
      field: 'weight',
      value: weight,
    });
  };

  const focusEditorInput = (id, field, { select = true } = {}) => {
    apiRef.current.setCellFocus(id, field);

    requestAnimationFrame(() => {
      const cellEl = apiRef.current.getCellElement?.(id, field);
      if (!cellEl) return;

      const editorInput = cellEl.querySelector('input, textarea, [contenteditable="true"], [tabindex="0"], button');

      if (editorInput) {
        editorInput.focus();
        if (select && typeof editorInput.select === 'function') {
          try {
            editorInput.select();
          } catch {}
        }
      }
    });
  };

  const handleArrowKeyNavigation = async (params, event, push) => {
    const key = event.key;
    const isArrowKey = key === 'ArrowDown' || key === 'ArrowUp';

    if (!isArrowKey || !event.ctrlKey) return;

    event.preventDefault();

    const rowId = params.id;
    if (apiRef.current.getRowMode(rowId) !== GridRowModes.Edit) return;

    apiRef.current.stopRowEditMode({ id: rowId });

    const orderedLines = getOrderedDocLines(values.docLine);
    const rowIndex = orderedLines.findIndex((line) => line.id === rowId);
    const offset = key === 'ArrowDown' ? 1 : -1;
    let nextIndex = rowIndex + offset;

    while (
      nextIndex >= 0 &&
      nextIndex < orderedLines.length &&
      orderedLines[nextIndex].lineStatus !== DocStatus.OPENED
    ) {
      nextIndex += offset;
    }

    const nextRow = orderedLines[nextIndex];

    if (key === 'ArrowDown' && !nextRow) {
      await handleClick(push);
      return;
    }

    if (!nextRow) return;

    if (apiRef.current.getRowMode(nextRow.id) === GridRowModes.View) {
      apiRef.current.startRowEditMode({ id: nextRow.id });
    }

    focusedFieldRef.current = params.field;
    focusEditorInput(nextRow.id, params.field);
  };

  const handleTabKeyNavigation = (params, event) => {
    const isTabOrEnter = event.key === 'Tab' || event.key === 'Enter';
    if (!isTabOrEnter) return;

    const rowId = params.id;

    const editableFields = getLineEditableFields(apiRef, rowId, { currentDocType, selectedValues });

    const currentField = params.field;
    const currentIdx = editableFields.indexOf(currentField);
    const dir = event.shiftKey ? -1 : 1;
    const nextIdx = currentIdx + dir;

    if (nextIdx < 0 || nextIdx >= editableFields.length) return;

    event.preventDefault();
    event.defaultMuiPrevented = true;

    const nextField = editableFields[nextIdx];

    focusedFieldRef.current = nextField;
    focusEditorInput(rowId, nextField);
  };

  const [updateLine, setUpdateLine] = useState(null);

  useEffect(() => {
    if (!updateLine) return;

    handleUpdatePastedLine(updateLine);
    setUpdateLine(null);
  }, [updateLine]);

  const handleUpdatePastedLine = async (data) => {
    const { itemCode, qty, unitPrice, newIndex } = data;
    await handleItemChange(newIndex, itemCode);
    handleQtyChange(newIndex, qty);
    handlePriceChange(newIndex, unitPrice ?? '0');
  };

  const [pendingUpdates, setPendingUpdates] = useState([]);

  useEffect(() => {
    if (pendingUpdates.length === 0) return;

    const process = async () => {
      const [next, ...rest] = pendingUpdates;
      const { itemCode, qty, unitPrice, newIndex } = next;

      await handleItemChange(newIndex, itemCode, true);
      handleQtyChange(newIndex, qty);
      handlePriceChange(newIndex, unitPrice ?? '0');

      setPendingUpdates(rest);
    };

    process();
  }, [pendingUpdates]);

  const handleClipboardPaste = async (params, insert) => {
    if (!validateDocumentDatesBeforeLineAdd()) return;

    const { data } = params;

    // kiinduló index a meglévő sorok száma alapján
    const startIndex = values.docLine.length;
    const firstVisOrder = getNextDocLineVisOrder(values.docLine);
    // csak a sikeresen felvett sorok növelik ezt
    let successOffset = 0;

    for (const row of data) {
      const [itemCode, qty, whsCode, unitPrice, internalNote] = row;

      const item = await getItemMetaByCode(itemCode);
      if (!item) {
        addNotification({
          content: `A(z) ${itemCode} cikk nem létezik !`,
          type: NotificationType.ERROR,
        });
        continue;
      }

      if (isNaN(parseFloat(qty))) {
        addNotification({
          content: `A mennyiség megadása kötelező a(z) ${itemCode} cikkhez!`,
          type: NotificationType.ERROR,
        });
        continue;
      }

      const newLine = {
        ...(await createNewLineData()),
        visOrder: firstVisOrder + successOffset,
        ...(whsCode && { whsCode }),
        ...(internalNote && { internalNote }),
      };

      const newIndex = startIndex + successOffset;
      successOffset += 1;
      insert(newIndex, newLine);
      setPendingUpdates((prev) => [...prev, { itemCode, qty, unitPrice, newIndex }]);
    }
  };

  return (
    <div className="overflow-x-auto">
      <div className="align-middle">
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
          <FieldArray name="docLine">
            {({ remove, push, insert }) => (
              <>
                <DatagridPremiumUi
                  key={datagridIdentifiers.docLine}
                  apiRef={apiRef}
                  datagridIdentifier={datagridIdentifiers.docLine}
                  aggregationModel={{}}
                  sx={documentLineGridSx}
                  height={gridHeight}
                  columns={columns(remove)}
                  rows={getOrderedDocLines(values?.docLine).filter((line) => line?.id != null)}
                  getRowClassName={(params) => (isPinnedRow(params) ? 'docline-pinned-row' : '')}
                  editMode="row"
                  slots={{
                    toolbar: EditToolbar,
                  }}
                  slotProps={{
                    toolbar: {
                      disabled,
                      push,
                      defaultField,
                      apiRef,
                      documentData,
                      selectedCompany,
                      autocompleteData,
                      handleClick,
                    },
                  }}
                  onCellDoubleClick={(params, event) => {
                    event.defaultMuiPrevented = true;
                    if (viewField) return;
                    if (params.field === 'bpNote') {
                      event.defaultMuiPrevented = true;
                      const valueIndex = getValueIndex(values, params.row.id);
                      setNoteDialogState({ open: true, valueIndex });
                      return;
                    }
                  }}
                  onCellClick={(params, event) => {
                    if (viewField) return;
                    if (params.row.lineStatus !== DocStatus.OPENED) return;
                    if (params.field === 'bpNote' && /(?:\r\n|\r|\n)/.test(params.row?.bpNote ?? '')) {
                      event.defaultMuiPrevented = true;
                      const valueIndex = getValueIndex(values, params.row.id);
                      setNoteDialogState({ open: true, valueIndex });
                      return;
                    }
                    const rowMode = apiRef.current.getRowMode(params.row.id);
                    if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.id });
                  }}
                  headerFilters={false}
                  getRowHeight={() => 'auto'}
                  onRowEditStop={() => {
                    focusedFieldRef.current = null;
                  }}
                  onCellKeyDown={(params, event) => {
                    handleArrowKeyNavigation(params, event, push);
                    handleTabKeyNavigation(params, event, push);
                  }}
                  isCellEditable={(params) =>
                    isPinnedRow(params) ||
                    (params.field === 'bpNote' && /(?:\r\n|\r|\n)/.test(params.row?.bpNote ?? ''))
                      ? false
                      : baseIsCellEditable(params)
                  }
                  onClipboardPasteStart={(params) => handleClipboardPaste(params, insert)}
                  pinnedRows={{ bottom: [pinnedLine] }}
                />
              </>
            )}
          </FieldArray>
        </div>
      </div>
      {/* ItemDialog külön, nem a FieldArray render-függvényében */}
      <ItemDialog
        showDialog={itemDialogOpen}
        onClose={selectItemFromDialog}
        selectedItemCode={itemDialogSelectedItemCode}
        hideRightPanel={itemDialogHideRightPanel}
        selectOnClose={itemDialogSelectOnClose}
        initialAddMode={itemDialogSelectOnClose && !itemDialogSelectedItemCode}
      />
      <ConfirmDialogUI
        show={projectPriceReloadState.open}
        onHide={handleProjectPriceReloadCancel}
        handleChangeElement={handleProjectPriceReloadConfirm}
        title="Projekt arak frissitese"
        message="A projekt modosult. Betoltsuk a projekthez tartozo arakat azoknal a soroknal, ahol van ervenyes projektar?"
        confirmButtonText="Arak frissitese"
      />
      <ItemCompanyCatalogDialog
        open={itemCompanyCatalogDialogState.open}
        onClose={handleItemCompanyCatalogDialogClose}
        rows={itemCompanyCatalogDialogState.rows}
        loading={itemCompanyCatalogDialogState.loading}
        onSelect={selectItemCompanyCatalogRow}
      />
      <ItemStockDialog
        open={itemStockDialogState.open}
        data={itemStockDialogState.data}
        onClose={handleItemStockDialogClose}
      />
      <DocumentPrevPricesDialog
        open={prevPricesDialogState.open}
        onClose={handlePrevPricesDialogClose}
        itemCode={prevPricesDialogState.line?.itemCode}
        itemName={prevPricesDialogState.line?.itemName}
        documentTypeId={currentDocType?.documentTypeId ?? parseInt(docTypeId)}
        currentDirection={currentDocType?.direction}
        docTypes={docTypes}
        initialPartnerId={values.companyId}
        initialPartnerName={values.companyName}
      />
      <SupportingDocumentAssignmentDialog
        open={supportingDocumentAssignmentDialogState.open}
        onClose={handleSupportingDocumentAssignmentDialogClose}
        itemCode={supportingDocumentAssignmentDialogState.itemCode}
        supportingDocumentAssignment={values.supportingDocumentAssignment}
        onChange={handleSupportingDocumentAssignmentChange}
        disabled={disabled}
        loadAllSupportingDocumentTypes
        batchManaged={
          autocompleteData.item?.find(
            (item) => item.itemCode === supportingDocumentAssignmentDialogState.itemCode
          )?.invManagementMethod === InvManagementMethod.BATCH
        }
      />
      <TextTemplateMenu
        contextMenu={templateContextMenu}
        templates={lineTextTemplates}
        onClose={() => setTemplateContextMenu(null)}
        onSelect={(template) => {
          const rowId = templateContextMenu?.rowId;
          const valueIndex = getValueIndex(values, rowId);
          if (valueIndex >= 0) {
            const templateBody = template.body ?? '';
            setFieldValue(`docLine.${valueIndex}.bpNote`, templateBody);
            if (apiRef.current.getRowMode(rowId) === GridRowModes.Edit) {
              apiRef.current.setEditCellValue({ id: rowId, field: 'bpNote', value: templateBody });
            }
          }
          setTemplateContextMenu(null);
        }}
      />
    </div>
  );
};

export default DocumentLine;
